<template>
    <!-- Desktop Version -->
    <div v-if="!isMobile" class="chat-app"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        :class="{ 'drag-over': isDragging }">
        <!-- Main Chat Area -->
        <div class="chat-main">
            <!-- Welcome Screen (when no conversation started) -->
            <div v-if="messages.length <= 1 && !isTyping" class="welcome-screen">
                <div class="welcome-content">
                    <div class="welcome-avatar">
                        <span class="mdi mdi-robot-outline"></span>
                    </div>
                    <h1 class="welcome-title">Hi{{ username ? ' ' + username.split(' ')[0] : '' }}!</h1>
                    <p class="welcome-subtitle">How can I help you today?</p>
                </div>
            </div>

            <!-- Messages Area (when conversation started) -->
            <div v-else class="messages-area" ref="scrollContainer">
                <div class="messages-inner">
                    <div v-for="(message, index) in messages" :key="index"
                        :class="['message-row', message.from === 'user' ? 'user' : 'assistant']">
                        <div class="message-content">
                            <div v-if="message.from !== 'user'" class="avatar">
                                <span class="mdi mdi-robot-outline"></span>
                            </div>
                            <div class="message-text" v-html="message.text"></div>
                        </div>
                    </div>

                    <!-- Typing Indicator -->
                    <div v-if="isTyping" class="message-row assistant">
                        <div class="message-content">
                            <div class="avatar">
                                <span class="mdi mdi-robot-outline"></span>
                            </div>
                            <div class="typing-indicator">
                                <span class="typing-text">{{ typingText }}</span>
                                <!-- Upload progress ring -->
                                <svg v-if="isUploading" class="upload-ring" width="28" height="28" viewBox="0 0 28 28">
                                    <circle cx="14" cy="14" r="11" fill="none" stroke="#e2e8f0" stroke-width="2.5"/>
                                    <circle cx="14" cy="14" r="11" fill="none" stroke="#3b82f6" stroke-width="2.5"
                                        stroke-linecap="round"
                                        :stroke-dasharray="69.1"
                                        :stroke-dashoffset="69.1 - (69.1 * uploadProgress / 100)"
                                        transform="rotate(-90 14 14)"
                                        style="transition: stroke-dashoffset 0.3s ease"/>
                                    <text x="14" y="14" text-anchor="middle" dominant-baseline="central"
                                        fill="#64748b" font-size="7" font-weight="600">{{ uploadProgress }}%</text>
                                </svg>
                                <!-- Normal typing dots (hidden during upload) -->
                                <div v-else class="typing-dots">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bottom"></div>
                </div>
            </div>

            <!-- Input Area - Fixed at Bottom -->
            <div class="input-area">
                <div class="input-wrapper">
                    <!-- Toolbar Row: Model Selector + Active Features -->
                    <div class="input-toolbar">
                        <!-- Model Selector -->
                        <v-menu :close-on-content-click="true">
                            <template v-slot:activator="{ props }">
                                <button v-bind="props" class="toolbar-model-btn" :disabled="deepResearch || use_flowcase_bot || use_company_context_addon || use_pptx_agent || use_proposal_agent || use_n8n_agent || use_transcript_agent">
                                    <span class="mdi mdi-robot-outline"></span>
                                    <span>{{ models.split('(')[0].trim() }}</span>
                                    <span class="model-version">{{ models.match(/\(([^)]+)\)/)?.[1] || '' }}</span>
                                    <span class="mdi mdi-chevron-down"></span>
                                </button>
                            </template>
                            <div class="dropdown-menu model-menu">
                                <button class="dropdown-item" @click="models = 'Auto Reasoning (GPT-5.4)'" :class="{ active: models === 'Auto Reasoning (GPT-5.4)' }">
                                    <div class="model-info">
                                        <div class="model-header">
                                            <span class="model-name">Auto Reasoning</span>
                                            <span class="model-version-tag">GPT-5.4</span>
                                        </div>
                                        <span class="model-desc">Fast, context-rich reasoning</span>
                                    </div>
                                    <span v-if="models === 'Auto Reasoning (GPT-5.4)'" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="models = 'Open Source (GPT-OSS)'" :class="{ active: models === 'Open Source (GPT-OSS)' }">
                                    <div class="model-info">
                                        <div class="model-header">
                                            <span class="model-name">Open Source</span>
                                            <span class="model-version-tag">GPT-OSS</span>
                                        </div>
                                        <span class="model-desc">Fast and efficient</span>
                                    </div>
                                    <span v-if="models === 'Open Source (GPT-OSS)'" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="models = 'Advanced Model (Opus-4.6)'" :class="{ active: models === 'Advanced Model (Opus-4.6)' }">
                                    <div class="model-info">
                                        <div class="model-header">
                                            <span class="model-name">Advanced Model</span>
                                            <span class="model-version-tag">Opus-4.6</span>
                                        </div>
                                        <span class="model-desc">Complex reasoning & analysis</span>
                                    </div>
                                    <span v-if="models === 'Advanced Model (Opus-4.6)'" class="mdi mdi-check"></span>
                                </button>
                            </div>
                        </v-menu>

                        <!-- Active Features Chips -->
                        <div v-if="deepResearch" class="chip">
                            <span class="mdi mdi-magnify-scan"></span>
                            <span>Deep Research</span>
                            <button @click="toggleDeepResearch">&times;</button>
                        </div>
                        <div v-if="use_flowcase_bot" class="chip">
                            <span class="mdi mdi-chart-box-outline"></span>
                            <span>References Bot</span>
                            <button @click="toggleReferenceBot">&times;</button>
                        </div>
                        <div v-if="use_company_context_addon" class="chip">
                            <span class="mdi mdi-link-variant"></span>
                            <span>Company Context</span>
                            <button @click="toggleCompanyContextAddon">&times;</button>
                        </div>
                        <div v-if="use_pptx_agent" class="chip">
                            <span class="mdi mdi-file-powerpoint-outline"></span>
                            <span>PowerPoint Agent</span>
                            <button @click="togglePptxAgent">&times;</button>
                        </div>
                        <div v-if="use_proposal_agent" class="chip">
                            <span class="mdi mdi-file-document-edit-outline"></span>
                            <span>Proposal Agent</span>
                            <button @click="toggleProposalAgent">&times;</button>
                        </div>
                        <div v-if="use_n8n_agent" class="chip">
                            <span class="mdi mdi-cog-transfer-outline"></span>
                            <span>Workflow Builder</span>
                            <button @click="toggleN8nAgent">&times;</button>
                        </div>
                        <div v-if="use_transcript_agent" class="chip">
                            <span class="mdi mdi-microphone-outline"></span>
                            <span>Transcript</span>
                            <button @click="toggleTranscriptAgent">&times;</button>
                        </div>
                        <div v-if="transcriptFile" class="chip file">
                            <span class="mdi mdi-waveform"></span>
                            <span>{{ transcriptFilename }}</span>
                            <button @click="onRemoveTranscriptFile">&times;</button>
                        </div>
                        <div v-if="fileupload && !use_pptx_agent" class="chip file">
                            <span class="mdi mdi-file-pdf-box"></span>
                            <span>{{ filename }}</span>
                            <v-progress-circular v-if="!convertingPdfDone" indeterminate size="14" width="2" color="primary"></v-progress-circular>
                            <button v-else @click="onRemoveFile">&times;</button>
                        </div>
                        <div v-if="fileupload && use_pptx_agent" class="chip file pptx-context">
                            <span class="mdi mdi-file-pdf-box"></span>
                            <span>{{ filename }} (PPTX-Kontext)</span>
                            <v-progress-circular v-if="!convertingPdfDone" indeterminate size="14" width="2" color="primary"></v-progress-circular>
                            <button v-else @click="onRemoveFile">&times;</button>
                        </div>
                    </div>

                    <!-- Main Input Container -->
                    <div class="input-container">
                        <!-- Plus Button -->
                        <v-menu location="top start" :close-on-content-click="true">
                            <template v-slot:activator="{ props }">
                                <button v-bind="props" class="icon-btn plus-btn">
                                    <span class="mdi mdi-plus"></span>
                                    <span v-if="hasAnyNewFeature()" class="new-badge"></span>
                                </button>
                            </template>
                            <div class="dropdown-menu">
                                <div class="dropdown-section-header first">Agents</div>
                                <button class="dropdown-item" @click="toggleDeepResearch" :class="{ active: deepResearch }">
                                    <span class="mdi mdi-magnify-scan"></span>
                                    <span>Deep Research</span>
                                    <span v-if="deepResearch" class="mdi mdi-check"></span>
                                </button>
                                <button v-if="has_flowcase_addon" class="dropdown-item" @click="toggleReferenceBot" :class="{ active: use_flowcase_bot }">
                                    <span class="mdi mdi-chart-box-outline"></span>
                                    <span>References Bot</span>
                                    <span v-if="use_flowcase_bot" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="toggleCompanyContextAddon" :class="{ active: use_company_context_addon }">
                                    <span class="mdi mdi-link-variant"></span>
                                    <span>Company Context</span>
                                    <span v-if="use_company_context_addon" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="togglePptxAgent" :class="{ active: use_pptx_agent }">
                                    <span class="mdi mdi-file-powerpoint-outline"></span>
                                    <span>PowerPoint Agent</span>
                                    <span v-if="use_pptx_agent" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="toggleProposalAgent" :class="{ active: use_proposal_agent }">
                                    <span class="mdi mdi-file-document-edit-outline"></span>
                                    <span>Proposal Agent</span>
                                    <span v-if="use_proposal_agent" class="mdi mdi-check"></span>
                                </button>
                                <button v-if="has_n8n_addon" class="dropdown-item" @click="toggleN8nAgent" :class="{ active: use_n8n_agent }">
                                    <span class="mdi mdi-cog-transfer-outline"></span>
                                    <span>Workflow Builder</span>
                                    <span v-if="use_n8n_agent" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="toggleTranscriptAgent" :class="{ active: use_transcript_agent }">
                                    <span class="mdi mdi-microphone-outline"></span>
                                    <span>Transcript</span>
                                    <span v-if="isNewFeature('transcript')" class="new-badge menu-badge"></span>
                                    <span v-if="use_transcript_agent" class="mdi mdi-check"></span>
                                </button>
                                <div class="dropdown-divider"></div>
                                <button class="dropdown-item" @click="onUploadFile">
                                    <span class="mdi mdi-paperclip"></span>
                                    <span>{{ use_n8n_agent ? 'Upload File' : 'Upload PDF' }}</span>
                                </button>
                            </div>
                        </v-menu>

                        <!-- Text Input -->
                        <div
                            class="rich-input"
                            :class="{ disabled: isPrintingText || inputDisabled, 'input-error': inputDisabled }"
                            :contenteditable="!inputDisabled"
                            :data-placeholder="inputDisabled ? 'n8n API Key erforderlich — bitte unter Settings hinterlegen' : isPrintingText ? 'Waiting for response...' : 'Message'"
                            @input="onRichInput"
                            @keydown.enter.exact.prevent="sendMessage"
                            @keydown.shift.enter.prevent="onShiftEnter"
                            @keydown.ctrl.enter.prevent="onShiftEnter"
                            @paste="onRichPaste"
                            ref="inputField"
                        ></div>

                        <!-- Send/Stop Button -->
                        <button v-if="!isPrintingText" class="icon-btn send-btn" @click="sendMessage" :disabled="!input.trim() && !transcriptFile">
                            <span class="mdi mdi-arrow-up"></span>
                        </button>
                        <button v-else class="icon-btn stop-btn" @click="stopTyping">
                            <span class="mdi mdi-stop"></span>
                        </button>
                    </div>
                </div>

                <!-- Footer -->
                <div class="input-footer">
                    <span>Powered by AI. Chat will be deleted after this session and will not be used for training.</span>
                    <span class="link" @click="overlayDisclaimer = true">Disclaimer</span>
                </div>
            </div>
        </div>

        <!-- Disclaimer Overlay -->
        <v-dialog v-model="overlayDisclaimer" max-width="800" scrollable>
            <v-card class="disclaimer-dialog">
                <div class="disclaimer-header">
                    <h2>Disclaimer</h2>
                </div>
                <div class="disclaimer-body">
                    <p class="lang-note">English version below</p>

                    <h3>1. Verwendungszweck und Status</h3>
                    <ul>
                        <li>Dieser digitale Assistent (Chatbot) wird von HPC bereitgestellt, um Mitarbeitende bei Recherchen, Ideenfindung und Textbausteinen zu unterstützen.</li>
                        <li>Das System befindet sich in einer Test-/Pilotphase und kann bei Bedarf eingeschränkt oder abgeschaltet werden.</li>
                    </ul>

                    <h3>2. Teilweise Web-Recherche</h3>
                    <ul>
                        <li>Um aktuelle Informationen einzuholen, nutzt der Chatbot bei Bedarf Bing Search innerhalb von Microsoft Azure. Dabei werden nur relevante Teilinhalte des Prompts an die Such-API übermittelt.</li>
                        <li>Eine vollständige Weitergabe der gesamten Unterhaltung an die Web-Suche erfolgt nicht.</li>
                    </ul>

                    <h3>3. Zulässige Eingaben</h3>
                    <ul>
                        <li><strong>Erlaubt:</strong> Interne Projektnamen, Kunden-/Kollegen-Namen, geschäftliche Daten, soweit für die Arbeit erforderlich.</li>
                        <li><strong>Bitte meiden:</strong> Hochsensible Daten (gemäß DSGVO Art. 9), z. B. Gesundheitsangaben, politische oder religiöse Überzeugungen u. Ä. Wenn absolut nötig, klären Sie das vorab mit Ihrer Datenschutzabteilung.</li>
                        <li><strong>Achtung bei streng vertraulichen Themen:</strong> Geben Sie hier nur das Nötigste ein. Bei Bedarf interne Kanäle (z. B. verschlüsselte Mails) nutzen.</li>
                    </ul>

                    <h3>4. Datenschutz & Verarbeitung</h3>
                    <ul>
                        <li><strong>Keine dauerhafte Speicherung:</strong> Eingaben werden nicht in einer HPC-eigenen Datenbank abgelegt. Sie verbleiben maximal 30 Tage in Azure-Logs zur Missbrauchsüberwachung.</li>
                        <li><strong>Keine Trainingsnutzung:</strong> Die Eingaben dienen nicht dem KI-Modelltraining. Microsoft/OpenAI hat darauf keinen Zugriff für Trainingszwecke.</li>
                        <li><strong>DSGVO-Konformität:</strong> HPC hat mit Microsoft Azure entsprechende Verträge (DPA) und richtet sich nach dem Prinzip der Datenminimierung.</li>
                    </ul>

                    <h3>5. Haftungsausschluss & Inhaltliche Grenzen</h3>
                    <ul>
                        <li><strong>Automatisierte Antworten:</strong> Der Chatbot generiert Texte mithilfe eines Sprachmodells und ggf. externer Webquellen. Diese können fehlerhaft, veraltet oder unvollständig sein.</li>
                        <li><strong>Keine verbindliche Beratung:</strong> Alle Ausgaben sind unverbindlich. Für kritische Entscheidungen ist eine fachliche Prüfung oder ein menschliches Review erforderlich.</li>
                        <li><strong>Haftung:</strong> HPC haftet nicht für Schäden, die aus dem Vertrauen auf Chatbot-Inhalte entstehen, soweit gesetzlich zulässig.</li>
                    </ul>

                    <h3>6. Web-Inhalte und Urheberrecht</h3>
                    <ul>
                        <li>Bei Web-Recherchen können urheberrechtlich geschützte Texte oder Ausschnitte zum Teil in die Antwort einfließen.</li>
                        <li>Wir bemühen uns, nur kurze Zitate oder Zusammenfassungen zu liefern. Eine kommerzielle oder unrechtmäßige Weiterverwendung liegt in der Verantwortung der Nutzenden.</li>
                    </ul>

                    <h3>7. Security & Missbrauch</h3>
                    <ul>
                        <li>Alle Datenverbindungen sind abgesichert, dennoch verbleibt ein Restrisiko (z. B. bei gezielten Angriffen).</li>
                        <li><strong>Missbrauch:</strong> Verstöße gegen geltendes Recht oder interne Firmenvorgaben (z. B. Eingabe sensibler Daten ohne Grund, Erzeugen schädlicher Inhalte) sind untersagt. HPC behält sich vor, Zugriffe einzuschränken oder zu sperren und ggf. weitere Schritte einzuleiten.</li>
                    </ul>

                    <h3>8. Konformität mit KI-Act und Data Act</h3>
                    <ul>
                        <li>HPC hat eine interne Risikobeurteilung durchgeführt (siehe interne Dokumentation) und berücksichtigt relevante Vorgaben des KI-Acts (Art. 9) sowie des EU Data Acts.</li>
                        <li>Aktuell keine Einstufung als „High Risk"; sollte sich der Einsatzbereich ändern (z. B. Personalentscheidungen, Finanz- oder Gesundheitsbereich), bedarf es einer erneuten Freigabe durch Compliance.</li>
                    </ul>

                    <h3>9. Änderungen und Kommunikation</h3>
                    <ul>
                        <li>HPC kann diesen Disclaimer jederzeit an neue technische, organisatorische oder rechtliche Anforderungen anpassen.</li>
                        <li>Über wesentliche Änderungen werden die Nutzenden informiert (Intranet, E-Mail o. Ä.).</li>
                    </ul>

                    <h3>10. Kontakt und Rückfragen</h3>
                    <p class="contact-block">
                        <strong>Allgemein/Sicherheit:</strong> Wir haben einen Datenschutzbeauftragten ernannt.<br>
                        Hamburger Hafen und Logistik Aktiengesellschaft<br>
                        Konzernbeauftragter für den Datenschutz<br>
                        Herr Dr. John Maurer<br>
                        Bei St. Annen 1<br>
                        20457 Hamburg<br>
                        Phone: +49 40 74008-0<br>
                        E-mail: info@hpc-hamburg.de
                    </p>

                    <p class="agreement-note"><strong>Mit der Nutzung dieses Assistenten stimmen Sie den oben genannten Bedingungen zu und nehmen sie zur Kenntnis.</strong></p>

                    <hr>

                    <h3>1. Purpose and Status</h3>
                    <ul>
                        <li>This digital assistant (chatbot) is provided by HPC to support employees in research, idea generation, and text modules.</li>
                        <li>The system is in a test/pilot phase and may be restricted or deactivated if necessary.</li>
                    </ul>

                    <h3>2. Partial Web Search</h3>
                    <ul>
                        <li>To retrieve up-to-date information, the chatbot uses Bing Search within Microsoft Azure when needed. Only relevant parts of the prompt are transmitted to the search API.</li>
                        <li>A complete transmission of the entire conversation to the web search does not take place.</li>
                    </ul>

                    <h3>3. Permitted Inputs</h3>
                    <ul>
                        <li><strong>Allowed:</strong> Internal project names, customer/colleague names, business data, as far as required for work purposes.</li>
                        <li><strong>Please avoid:</strong> Highly sensitive data (as per GDPR Art. 9), e.g., health information, political or religious beliefs, etc. If absolutely necessary, consult your data protection department beforehand.</li>
                        <li><strong>Caution with strictly confidential topics:</strong> Only enter what is necessary. Use internal channels (e.g., encrypted emails) if needed.</li>
                    </ul>

                    <h3>4. Data Protection & Processing</h3>
                    <ul>
                        <li><strong>No permanent storage:</strong> Inputs are not stored in an HPC-owned database. They remain in Azure logs for a maximum of 30 days for abuse monitoring.</li>
                        <li><strong>No training usage:</strong> Inputs are not used for AI model training. Microsoft/OpenAI has no access for training purposes.</li>
                        <li><strong>GDPR compliance:</strong> HPC has appropriate contracts (DPA) with Microsoft Azure and follows the principle of data minimization.</li>
                    </ul>

                    <h3>5. Liability Disclaimer & Content Limitations</h3>
                    <ul>
                        <li><strong>Automated responses:</strong> The chatbot generates text using a language model and, if applicable, external web sources. These may be incorrect, outdated, or incomplete.</li>
                        <li><strong>No binding advice:</strong> All outputs are non-binding. Critical decisions require expert review or human verification.</li>
                        <li><strong>Liability:</strong> HPC is not liable for damages resulting from reliance on chatbot-generated content, to the extent permitted by law.</li>
                    </ul>

                    <h3>6. Web Content and Copyright</h3>
                    <ul>
                        <li>Web searches may include copyrighted texts or excerpts in responses.</li>
                        <li>We strive to provide only short quotes or summaries. Users are responsible for any commercial or unlawful reuse.</li>
                    </ul>

                    <h3>7. Security & Misuse</h3>
                    <ul>
                        <li>All data connections are secured, but a residual risk remains (e.g., targeted attacks).</li>
                        <li><strong>Misuse:</strong> Violations of applicable laws or internal company policies (e.g., entering sensitive data without justification, generating harmful content) are prohibited. HPC reserves the right to restrict or block access and take further action if necessary.</li>
                    </ul>

                    <h3>8. Compliance with AI Act and Data Act</h3>
                    <ul>
                        <li>HPC has conducted an internal risk assessment (see internal documentation) and considers relevant requirements of the AI Act (Art. 9) and the EU Data Act.</li>
                        <li>Currently, no classification as "High Risk"; if the scope changes (e.g., personnel decisions, financial or healthcare applications), a new approval by Compliance is required.</li>
                    </ul>

                    <h3>9. Changes and Communication</h3>
                    <ul>
                        <li>HPC may adjust this disclaimer at any time to reflect new technical, organizational, or legal requirements.</li>
                        <li>Users will be informed of significant changes (Intranet, email, etc.).</li>
                    </ul>

                    <h3>10. Contact and Inquiries</h3>
                    <p class="contact-block">
                        <strong>General/Security:</strong> We have appointed a data protection officer.<br>
                        Hamburger Hafen und Logistik Aktiengesellschaft<br>
                        Konzernbeauftragter für den Datenschutz<br>
                        Herr Dr. John Maurer<br>
                        Bei St. Annen 1<br>
                        20457 Hamburg<br>
                        Phone: +49 40 74008-0<br>
                        E-mail: info@hpc-hamburg.de
                    </p>

                    <p class="agreement-note"><strong>By using this assistant, you agree to and acknowledge the above conditions.</strong></p>
                </div>
                <div class="disclaimer-footer">
                    <button class="primary-btn" @click="overlayDisclaimer = false">Close</button>
                </div>
            </v-card>
        </v-dialog>

        <!-- Hidden File Input -->
        <input type="file" ref="fileInput" style="display: none" :accept="use_n8n_agent ? '*/*' : 'application/pdf'" @change="handleFileUpload" />
        <!-- Hidden Audio File Input for Transcript Agent -->
        <input type="file" ref="audioFileInput" style="display: none" accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.caf" @change="handleAudioFileUpload" />

        <!-- Loading Overlay -->
        <div v-if="loading" class="loading-overlay">
            <v-progress-circular color="primary" indeterminate size="48"></v-progress-circular>
        </div>
    </div>

    <!-- Mobile Version -->
    <div v-else class="chat-app mobile"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        :class="{ 'drag-over': isDragging }">
        <!-- Welcome Screen (when no conversation started) -->
        <div v-if="messages.length <= 1 && !isTyping" class="welcome-screen">
            <div class="welcome-content">
                <div class="welcome-avatar">
                    <span class="mdi mdi-robot-outline"></span>
                </div>
                <h1 class="welcome-title">Hi{{ username ? ' ' + username.split(' ')[0] : '' }}!</h1>
                <p class="welcome-subtitle">How can I help you today?</p>
            </div>
        </div>

        <!-- Messages Area (when conversation started) -->
        <div v-else class="messages-area" ref="scrollContainer">
            <div class="messages-inner">
                <div v-for="(message, index) in messages" :key="index"
                    :class="['message-row', message.from === 'user' ? 'user' : 'assistant']">
                    <div class="message-content">
                        <div v-if="message.from !== 'user'" class="avatar">
                            <span class="mdi mdi-robot-outline"></span>
                        </div>
                        <div class="message-text" v-html="message.text"></div>
                    </div>
                </div>

                <!-- Typing Indicator -->
                <div v-if="isTyping" class="message-row assistant">
                    <div class="message-content">
                        <div class="avatar">
                            <span class="mdi mdi-robot-outline"></span>
                        </div>
                        <div class="typing-indicator">
                            <span class="typing-text">{{ typingText }}</span>
                            <!-- Upload progress ring -->
                            <svg v-if="isUploading" class="upload-ring" width="28" height="28" viewBox="0 0 28 28">
                                <circle cx="14" cy="14" r="11" fill="none" stroke="#e2e8f0" stroke-width="2.5"/>
                                <circle cx="14" cy="14" r="11" fill="none" stroke="#3b82f6" stroke-width="2.5"
                                    stroke-linecap="round"
                                    :stroke-dasharray="69.1"
                                    :stroke-dashoffset="69.1 - (69.1 * uploadProgress / 100)"
                                    transform="rotate(-90 14 14)"
                                    style="transition: stroke-dashoffset 0.3s ease"/>
                                <text x="14" y="14" text-anchor="middle" dominant-baseline="central"
                                    fill="#64748b" font-size="7" font-weight="600">{{ uploadProgress }}%</text>
                            </svg>
                            <!-- Normal typing dots (hidden during upload) -->
                            <div v-else class="typing-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="bottom"></div>
            </div>
        </div>

        <!-- Mobile Input Area -->
        <div class="mobile-input-area">
            <div class="input-wrapper">
                <!-- Toolbar Row: Model Selector + Active Features -->
                <div class="input-toolbar">
                    <!-- Model Selector -->
                    <v-menu :close-on-content-click="true">
                        <template v-slot:activator="{ props }">
                            <button v-bind="props" class="toolbar-model-btn" :disabled="deepResearch || use_flowcase_bot || use_company_context_addon || use_pptx_agent || use_proposal_agent || use_transcript_agent">
                                <span class="mdi mdi-robot-outline"></span>
                                <span>{{ models.split('(')[0].trim() }}</span>
                                <span class="model-version">{{ models.match(/\(([^)]+)\)/)?.[1] || '' }}</span>
                                <span class="mdi mdi-chevron-down"></span>
                            </button>
                        </template>
                        <div class="dropdown-menu model-menu">
                            <button class="dropdown-item" @click="models = 'Auto Reasoning (GPT-5.4)'" :class="{ active: models === 'Auto Reasoning (GPT-5.4)' }">
                                <span>Auto Reasoning</span>
                                <span class="model-version-tag">GPT-5.4</span>
                                <span v-if="models === 'Auto Reasoning (GPT-5.4)'" class="mdi mdi-check"></span>
                            </button>
                            <button class="dropdown-item" @click="models = 'Open Source (GPT-OSS)'" :class="{ active: models === 'Open Source (GPT-OSS)' }">
                                <span>Open Source</span>
                                <span class="model-version-tag">GPT-OSS</span>
                                <span v-if="models === 'Open Source (GPT-OSS)'" class="mdi mdi-check"></span>
                            </button>
                            <button class="dropdown-item" @click="models = 'Advanced Model (Opus-4.6)'" :class="{ active: models === 'Advanced Model (Opus-4.6)' }">
                                <span>Advanced Model</span>
                                <span class="model-version-tag">Opus-4.6</span>
                                <span v-if="models === 'Advanced Model (Opus-4.6)'" class="mdi mdi-check"></span>
                            </button>
                        </div>
                    </v-menu>

                    <!-- Active Features Chips -->
                    <div v-if="deepResearch" class="chip">
                        <span>Deep Research</span>
                        <button @click="toggleDeepResearch">&times;</button>
                    </div>
                    <div v-if="use_flowcase_bot" class="chip">
                        <span>References</span>
                        <button @click="toggleReferenceBot">&times;</button>
                    </div>
                    <div v-if="use_company_context_addon" class="chip">
                        <span>Context</span>
                        <button @click="toggleCompanyContextAddon">&times;</button>
                    </div>
                    <div v-if="use_pptx_agent" class="chip">
                        <span>PowerPoint</span>
                        <button @click="togglePptxAgent">&times;</button>
                    </div>
                    <div v-if="use_proposal_agent" class="chip">
                        <span>Proposal</span>
                        <button @click="toggleProposalAgent">&times;</button>
                    </div>
                    <div v-if="use_n8n_agent" class="chip">
                        <span>Workflow Builder</span>
                        <button @click="toggleN8nAgent">&times;</button>
                    </div>
                    <div v-if="use_transcript_agent" class="chip">
                        <span>Transcript</span>
                        <button @click="toggleTranscriptAgent">&times;</button>
                    </div>
                    <div v-if="transcriptFile" class="chip file">
                        <span class="mdi mdi-waveform"></span>
                        <span>{{ transcriptFilename }}</span>
                        <button @click="onRemoveTranscriptFile">&times;</button>
                    </div>
                    <div v-if="fileupload && !use_pptx_agent" class="chip file">
                        <span class="mdi mdi-file-pdf-box"></span>
                        <span>{{ filename }}</span>
                        <button @click="onRemoveFile">&times;</button>
                    </div>
                    <div v-if="fileupload && use_pptx_agent" class="chip file pptx-context">
                        <span class="mdi mdi-file-pdf-box"></span>
                        <span>{{ filename }} (Kontext)</span>
                        <button @click="onRemoveFile">&times;</button>
                    </div>
                </div>

                <div class="input-container">
                    <v-menu location="top start" :close-on-content-click="true">
                        <template v-slot:activator="{ props }">
                            <button v-bind="props" class="icon-btn plus-btn">
                                <span class="mdi mdi-plus"></span>
                                <span v-if="hasAnyNewFeature()" class="new-badge"></span>
                            </button>
                        </template>
                        <div class="dropdown-menu">
                            <button class="dropdown-item" @click="toggleDeepResearch">
                                <span class="mdi mdi-magnify-scan"></span>
                                <span>Deep Research</span>
                            </button>
                            <button v-if="has_flowcase_addon" class="dropdown-item" @click="toggleReferenceBot">
                                <span class="mdi mdi-chart-box-outline"></span>
                                <span>References Bot</span>
                            </button>
                            <button class="dropdown-item" @click="toggleCompanyContextAddon">
                                <span class="mdi mdi-link-variant"></span>
                                <span>Company Context</span>
                            </button>
                            <button class="dropdown-item" @click="togglePptxAgent">
                                <span class="mdi mdi-file-powerpoint-outline"></span>
                                <span>PowerPoint Agent</span>
                            </button>
                            <button class="dropdown-item" @click="toggleProposalAgent">
                                <span class="mdi mdi-file-document-edit-outline"></span>
                                <span>Proposal Agent</span>
                            </button>
                            <button v-if="has_n8n_addon" class="dropdown-item" @click="toggleN8nAgent">
                                <span class="mdi mdi-cog-transfer-outline"></span>
                                <span>Workflow Builder</span>
                            </button>
                            <button class="dropdown-item" @click="toggleTranscriptAgent">
                                <span class="mdi mdi-microphone-outline"></span>
                                <span>Transcript</span>
                                <span v-if="isNewFeature('transcript')" class="new-badge menu-badge"></span>
                            </button>
                            <div class="dropdown-divider"></div>
                            <button class="dropdown-item" @click="onUploadFile">
                                <span class="mdi mdi-paperclip"></span>
                                <span>{{ use_n8n_agent ? 'Upload File' : 'Upload PDF' }}</span>
                            </button>
                        </div>
                    </v-menu>

                    <div
                        class="rich-input"
                        :class="{ disabled: isPrintingText || inputDisabled, 'input-error': inputDisabled }"
                        :contenteditable="!inputDisabled"
                        :data-placeholder="inputDisabled ? 'n8n API Key erforderlich — bitte unter Settings hinterlegen' : 'Message...'"
                        @input="onRichInput"
                        @keydown.enter.exact.prevent="sendMessage"
                        @keydown.shift.enter.prevent="onShiftEnter"
                        @keydown.ctrl.enter.prevent="onShiftEnter"
                        @paste="onRichPaste"
                        ref="inputFieldMobile"
                    ></div>

                    <button v-if="!isPrintingText" class="icon-btn send-btn" @click="sendMessage" :disabled="!input.trim() && !transcriptFile">
                        <span class="mdi mdi-arrow-up"></span>
                    </button>
                    <button v-else class="icon-btn stop-btn" @click="stopTyping">
                        <span class="mdi mdi-stop"></span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Disclaimer -->
        <v-dialog v-model="overlayDisclaimer" fullscreen>
            <v-card class="disclaimer-dialog mobile">
                <div class="disclaimer-header">
                    <h2>Disclaimer</h2>
                    <button class="close-btn" @click="overlayDisclaimer = false">
                        <span class="mdi mdi-close"></span>
                    </button>
                </div>
                <div class="disclaimer-body">
                    <p class="lang-note">English version below</p>

                    <h3>1. Verwendungszweck und Status</h3>
                    <ul>
                        <li>Dieser digitale Assistent (Chatbot) wird von HPC bereitgestellt, um Mitarbeitende bei Recherchen, Ideenfindung und Textbausteinen zu unterstützen.</li>
                        <li>Das System befindet sich in einer Test-/Pilotphase und kann bei Bedarf eingeschränkt oder abgeschaltet werden.</li>
                    </ul>

                    <h3>2. Teilweise Web-Recherche</h3>
                    <ul>
                        <li>Um aktuelle Informationen einzuholen, nutzt der Chatbot bei Bedarf Bing Search innerhalb von Microsoft Azure. Dabei werden nur relevante Teilinhalte des Prompts an die Such-API übermittelt.</li>
                        <li>Eine vollständige Weitergabe der gesamten Unterhaltung an die Web-Suche erfolgt nicht.</li>
                    </ul>

                    <h3>3. Zulässige Eingaben</h3>
                    <ul>
                        <li><strong>Erlaubt:</strong> Interne Projektnamen, Kunden-/Kollegen-Namen, geschäftliche Daten, soweit für die Arbeit erforderlich.</li>
                        <li><strong>Bitte meiden:</strong> Hochsensible Daten (gemäß DSGVO Art. 9), z. B. Gesundheitsangaben, politische oder religiöse Überzeugungen u. Ä.</li>
                        <li><strong>Achtung bei streng vertraulichen Themen:</strong> Geben Sie hier nur das Nötigste ein.</li>
                    </ul>

                    <h3>4. Datenschutz & Verarbeitung</h3>
                    <ul>
                        <li><strong>Keine dauerhafte Speicherung:</strong> Eingaben verbleiben maximal 30 Tage in Azure-Logs.</li>
                        <li><strong>Keine Trainingsnutzung:</strong> Die Eingaben dienen nicht dem KI-Modelltraining.</li>
                        <li><strong>DSGVO-Konformität:</strong> HPC hat mit Microsoft Azure entsprechende Verträge (DPA).</li>
                    </ul>

                    <h3>5. Haftungsausschluss & Inhaltliche Grenzen</h3>
                    <ul>
                        <li><strong>Automatisierte Antworten:</strong> Texte können fehlerhaft, veraltet oder unvollständig sein.</li>
                        <li><strong>Keine verbindliche Beratung:</strong> Alle Ausgaben sind unverbindlich.</li>
                        <li><strong>Haftung:</strong> HPC haftet nicht für Schäden aus Chatbot-Inhalten.</li>
                    </ul>

                    <h3>6. Web-Inhalte und Urheberrecht</h3>
                    <ul>
                        <li>Bei Web-Recherchen können urheberrechtlich geschützte Texte einfließen.</li>
                        <li>Eine kommerzielle Weiterverwendung liegt in der Verantwortung der Nutzenden.</li>
                    </ul>

                    <h3>7. Security & Missbrauch</h3>
                    <ul>
                        <li>Alle Datenverbindungen sind abgesichert, Restrisiko verbleibt.</li>
                        <li>Verstöße sind untersagt. HPC behält sich vor, Zugriffe zu sperren.</li>
                    </ul>

                    <h3>8. Konformität mit KI-Act und Data Act</h3>
                    <ul>
                        <li>HPC berücksichtigt relevante Vorgaben des KI-Acts und EU Data Acts.</li>
                        <li>Aktuell keine Einstufung als „High Risk".</li>
                    </ul>

                    <h3>9. Änderungen und Kommunikation</h3>
                    <ul>
                        <li>HPC kann diesen Disclaimer jederzeit anpassen.</li>
                    </ul>

                    <h3>10. Kontakt</h3>
                    <p class="contact-block">
                        Datenschutzbeauftragter: Dr. John Maurer<br>
                        Bei St. Annen 1, 20457 Hamburg<br>
                        Phone: +49 40 74008-0<br>
                        E-mail: info@hpc-hamburg.de
                    </p>

                    <p class="agreement-note"><strong>Mit der Nutzung stimmen Sie den Bedingungen zu.</strong></p>

                    <hr>

                    <h3>1. Purpose and Status</h3>
                    <ul>
                        <li>This chatbot is provided by HPC to support employees in research and idea generation.</li>
                        <li>The system is in a test/pilot phase.</li>
                    </ul>

                    <h3>2. Partial Web Search</h3>
                    <ul>
                        <li>The chatbot uses Bing Search within Microsoft Azure when needed.</li>
                        <li>Only relevant parts of the prompt are transmitted.</li>
                    </ul>

                    <h3>3. Permitted Inputs</h3>
                    <ul>
                        <li><strong>Allowed:</strong> Internal project names, business data as required.</li>
                        <li><strong>Please avoid:</strong> Highly sensitive data (GDPR Art. 9).</li>
                    </ul>

                    <h3>4. Data Protection & Processing</h3>
                    <ul>
                        <li><strong>No permanent storage:</strong> Max 30 days in Azure logs.</li>
                        <li><strong>No training usage:</strong> Inputs not used for AI training.</li>
                        <li><strong>GDPR compliance:</strong> HPC has DPA with Microsoft Azure.</li>
                    </ul>

                    <h3>5. Liability Disclaimer</h3>
                    <ul>
                        <li>Responses may be incorrect or incomplete.</li>
                        <li>All outputs are non-binding.</li>
                    </ul>

                    <h3>6. Web Content and Copyright</h3>
                    <ul>
                        <li>Web searches may include copyrighted texts.</li>
                        <li>Users are responsible for any reuse.</li>
                    </ul>

                    <h3>7. Security & Misuse</h3>
                    <ul>
                        <li>All data connections are secured.</li>
                        <li>Misuse is prohibited. HPC may restrict access.</li>
                    </ul>

                    <h3>8. AI Act and Data Act Compliance</h3>
                    <ul>
                        <li>HPC considers AI Act and EU Data Act requirements.</li>
                    </ul>

                    <h3>9. Changes</h3>
                    <ul>
                        <li>HPC may adjust this disclaimer at any time.</li>
                    </ul>

                    <h3>10. Contact</h3>
                    <p class="contact-block">
                        Data Protection Officer: Dr. John Maurer<br>
                        Bei St. Annen 1, 20457 Hamburg<br>
                        Phone: +49 40 74008-0<br>
                        E-mail: info@hpc-hamburg.de
                    </p>

                    <p class="agreement-note"><strong>By using this assistant, you agree to the above conditions.</strong></p>
                </div>
                <div class="disclaimer-footer">
                    <button class="primary-btn" @click="overlayDisclaimer = false">I Understand</button>
                </div>
            </v-card>
        </v-dialog>

        <input type="file" ref="fileInput" style="display: none" :accept="use_n8n_agent ? '*/*' : 'application/pdf'" @change="handleFileUpload" />
        <input type="file" ref="audioFileInput" style="display: none" accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.caf" @change="handleAudioFileUpload" />

        <div v-if="loading" class="loading-overlay">
            <v-progress-circular color="primary" indeterminate size="40"></v-progress-circular>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import config from '../config.js';
import { marked } from 'marked';
import * as XLSX from 'xlsx';

const DEFAULT_TYPING_TEXT = "Thinking";
const WEBSEARCH_TYPING_TEXT = "Searching the web";
const PDF_TYPING_TEXT = "Analysing PDF";
const DEEPRESEARCH_TYPING_TEXT = "Deep Research in progress...";
const SUMMARY_TYPING_TEXT = "Summarizing";
const PROGRESS_REFERENCESBOT = "Consulting References Bot...";
const PROGRESS_COMPANYCONTEXT = "Searching Company Context...";
const PROGRESS_PPTX = "Creating PowerPoint...";
const PROGRESS_N8N = "Working on it...";
// Lightweight LLM call via backend (for agent conversation flows)
async function openai_client_call(prompt) {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${config.baseURL}/api/v1/llm-chat/`, { prompt }, {
        headers: { 'Authorization': `Token ${token}` },
        timeout: 30000,
    });
    return response.data.result;
}

export default {
    props: {
        isMobile: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            input: '',
            messages: [],
            loading: true,
            isTyping: false,
            isPrintingText: false,
            drawer: false,
            selectedChatId: 0,
            chats: [],
            new_thread: true,
            username: '',
            email: '',
            fileupload: false,
            fileprocessing: true,
            filename: '',
            selectedFile: undefined,
            filecontent: '',
            fileerror: false,
            fileerror_message: '',
            overlayDisclaimer: false,
            convertingPdfDone: false,
            isDragging: false,
            modelMenu: false,
            models: 'Auto Reasoning (GPT-5.4)',
            pdf_dialog: false,
            promiseResolve: null,
            pdf_context: false,
            deepResearch: false,
            typingText: DEFAULT_TYPING_TEXT,
            has_flowcase_addon: false,
            has_n8n_addon: false,
            n8n_api_key_set: false,
            n8n_key_checking: false,
            use_flowcase_bot: false,
            thread_id: undefined,
            use_company_context_addon: false,
            use_pptx_agent: false,
            use_proposal_agent: false,
            use_n8n_agent: false,
            inputDisabled: false,
            use_transcript_agent: false,
            transcriptFile: null,
            transcriptFilename: '',
            uploadProgress: 0,
            isUploading: false,
            // Transcript Agent conversation state:
            // null = not started, 'awaiting_confirm' = waiting for user yes/no, 'submitted' = sent to n8n
            transcriptState: null,
            transcriptEmail: '',
            transcriptFileId: null,
            newFeatures: {},
            currentTaskId: null,
        };
    },
    mounted() {
        document.title = 'DISlog Digital Assistant';
        this.initNewFeatures();
        setTimeout(() => {
            this.loading = false;
        }, 500);

        // Event delegation for copy-to-clipboard buttons in v-html content
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.copy-transcript-btn');
            if (!btn) return;
            e.preventDefault();
            e.stopPropagation();
            const text = btn.getAttribute('data-transcript');
            if (!text) return;

            // Decode HTML entities back to plain text
            const decoded = text.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
            const label = btn.querySelector('.copy-label');

            const onSuccess = () => {
                if (label) {
                    label.textContent = 'Copied!';
                    setTimeout(() => { label.textContent = 'Copy'; }, 2000);
                }
            };

            // Try modern API first, fallback to execCommand for HTTP/iOS
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(decoded).then(onSuccess).catch(() => {
                    this.fallbackCopy(decoded) && onSuccess();
                });
            } else {
                this.fallbackCopy(decoded) && onSuccess();
            }
        });

        // Handle Share Target: check if the app was opened via "Share with" from another app
        this.handleShareTargetAudio();
    },
    beforeDestroy() {
        if (this.$refs.scrollContainer) {
            const exportButtons = this.$refs.scrollContainer.querySelectorAll('.exportButton');
            exportButtons.forEach(btn => {
                btn.removeEventListener('click', this.exportToExcel);
            });
            const pptxButtons = this.$refs.scrollContainer.querySelectorAll('.pptxDownloadButton');
            pptxButtons.forEach(btn => {
                btn.removeEventListener('click', this.downloadPptx);
            });
        }
    },
    created() {
        this.getUser();
    },
    methods: {
        initNewFeatures() {
            // Register new features here: key → timestamp when introduced
            const featureRegistry = {
                transcript: 1773532800000, // 2026-03-14
            };
            const twoWeeks = 14 * 24 * 60 * 60 * 1000;
            const stored = JSON.parse(localStorage.getItem('dismissed_features') || '{}');
            const features = {};
            for (const [key, introduced] of Object.entries(featureRegistry)) {
                if (stored[key]) { features[key] = false; continue; }
                features[key] = (Date.now() - introduced) < twoWeeks;
            }
            this.newFeatures = features;
        },
        isNewFeature(key) {
            return !!this.newFeatures[key];
        },
        hasAnyNewFeature() {
            return Object.values(this.newFeatures).some(v => v);
        },
        dismissFeature(key) {
            this.newFeatures = { ...this.newFeatures, [key]: false };
            const stored = JSON.parse(localStorage.getItem('dismissed_features') || '{}');
            stored[key] = true;
            localStorage.setItem('dismissed_features', JSON.stringify(stored));
        },
        formatUserText(text) {
            // Escape HTML to prevent XSS
            const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const lines = escaped.split('\n');
            let html = '';
            let inOl = false;
            let inUl = false;

            for (const line of lines) {
                const olMatch = line.match(/^\s*(\d+)\.\s+(.*)/);
                const ulMatch = line.match(/^\s*[-•]\s+(.*)/);

                if (olMatch) {
                    if (!inOl) { if (inUl) { html += '</ul>'; inUl = false; } html += '<ol>'; inOl = true; }
                    html += `<li>${olMatch[2]}</li>`;
                } else if (ulMatch) {
                    if (!inUl) { if (inOl) { html += '</ol>'; inOl = false; } html += '<ul>'; inUl = true; }
                    html += `<li>${ulMatch[1]}</li>`;
                } else {
                    if (inOl) { html += '</ol>'; inOl = false; }
                    if (inUl) { html += '</ul>'; inUl = false; }
                    html += (html ? '<br>' : '') + line;
                }
            }
            if (inOl) html += '</ol>';
            if (inUl) html += '</ul>';
            return html;
        },
        getRichInputEl() {
            return this.$refs.inputField || this.$refs.inputFieldMobile;
        },
        getPlainTextFromEl(el) {
            // Only <br> counts as a real line break.
            // Chrome auto-wraps contenteditable content in <div>/<p> — ignore those for newlines.
            const parts = [];
            const walk = (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    parts.push(node.textContent);
                } else if (node.nodeName === 'BR') {
                    parts.push('\n');
                } else {
                    for (const child of node.childNodes) {
                        walk(child);
                    }
                }
            };
            walk(el);
            return parts.join('');
        },
        onRichInput(event) {
            const el = event.target;
            this.input = this.getPlainTextFromEl(el);
            // Auto-resize
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 200) + 'px';
        },
        onShiftEnter() {
            // Insert a <br> for Shift+Enter so getPlainTextFromEl picks it up as a real newline
            document.execCommand('insertLineBreak');
        },
        onRichPaste(event) {
            event.preventDefault();
            const text = (event.clipboardData || window.clipboardData).getData('text/plain');
            document.execCommand('insertText', false, text);
        },
        applyLiveFormatting(el) {
            const text = this.input;
            const lines = text.split('\n');
            const hasLists = lines.some(l => /^\s*(\d+\.\s+|[-•]\s+)/.test(l));
            if (!hasLists) return; // No formatting needed, keep native editing

            // Save cursor position
            const sel = window.getSelection();
            let cursorOffset = 0;
            if (sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                const preRange = range.cloneRange();
                preRange.selectNodeContents(el);
                preRange.setEnd(range.startContainer, range.startOffset);
                cursorOffset = preRange.toString().length;
            }

            // Build formatted HTML
            let html = '';
            let inOl = false;
            let inUl = false;
            for (const line of lines) {
                const olMatch = line.match(/^(\s*\d+\.\s+)(.*)/);
                const ulMatch = line.match(/^(\s*[-•]\s+)(.*)/);
                if (olMatch) {
                    if (!inOl) { if (inUl) { html += '</ul>'; inUl = false; } html += '<ol>'; inOl = true; }
                    html += `<li><span class="list-prefix">${olMatch[1]}</span>${olMatch[2]}</li>`;
                } else if (ulMatch) {
                    if (!inUl) { if (inOl) { html += '</ol>'; inOl = false; } html += '<ul>'; inUl = true; }
                    html += `<li><span class="list-prefix">${ulMatch[1]}</span>${ulMatch[2]}</li>`;
                } else {
                    if (inOl) { html += '</ol>'; inOl = false; }
                    if (inUl) { html += '</ul>'; inUl = false; }
                    html += (html ? '<br>' : '') + (line || '<br>');
                }
            }
            if (inOl) html += '</ol>';
            if (inUl) html += '</ul>';

            el.innerHTML = html;

            // Restore cursor position
            this.restoreCursor(el, cursorOffset);
        },
        restoreCursor(el, targetOffset) {
            const sel = window.getSelection();
            const range = document.createRange();
            let currentOffset = 0;
            let found = false;

            const walk = (node) => {
                if (found) return;
                if (node.nodeType === Node.TEXT_NODE) {
                    if (currentOffset + node.textContent.length >= targetOffset) {
                        range.setStart(node, targetOffset - currentOffset);
                        range.collapse(true);
                        found = true;
                        return;
                    }
                    currentOffset += node.textContent.length;
                } else {
                    for (const child of node.childNodes) {
                        walk(child);
                        if (found) return;
                    }
                }
            };
            walk(el);
            if (found) {
                sel.removeAllRanges();
                sel.addRange(range);
            } else {
                // Place cursor at end
                range.selectNodeContents(el);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
            }
        },
        autoResize(event) {
            const textarea = event.target;
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
        },
        _activateAgent(name) {
            // Alle Agents deaktivieren
            this.deepResearch = false;
            this.use_flowcase_bot = false;
            this.use_company_context_addon = false;
            this.use_pptx_agent = false;
            this.use_proposal_agent = false;
            this.use_n8n_agent = false;
            this.use_transcript_agent = false;
            // Gewuenschten Agent aktivieren
            const agentMap = {
                'pptx': 'use_pptx_agent',
                'proposal': 'use_proposal_agent',
                'flowcase': 'use_flowcase_bot',
                'transcript': 'use_transcript_agent',
                'deep_research': 'deepResearch',
            };
            const prop = agentMap[name];
            if (prop) {
                this[prop] = true;
                console.log(`Agent switched to: ${name}`);
            }
        },
        toggleDeepResearch() {
            this.deepResearch = !this.deepResearch;
            if (this.deepResearch) {
                this.use_flowcase_bot = false;
                this.use_company_context_addon = false;
                this.use_pptx_agent = false;
                this.use_proposal_agent = false;
                this.use_n8n_agent = false;
                this.use_transcript_agent = false;
            }
        },
        toggleCompanyContextAddon() {
            this.use_company_context_addon = !this.use_company_context_addon;
            if (this.use_company_context_addon) {
                this.deepResearch = false;
                this.use_flowcase_bot = false;
                this.use_pptx_agent = false;
                this.use_proposal_agent = false;
                this.use_n8n_agent = false;
                this.use_transcript_agent = false;
            }
        },
        toggleReferenceBot() {
            this.use_flowcase_bot = !this.use_flowcase_bot;
            if (this.use_flowcase_bot) {
                this.deepResearch = false;
                this.use_company_context_addon = false;
                this.use_pptx_agent = false;
                this.use_proposal_agent = false;
                this.use_n8n_agent = false;
                this.use_transcript_agent = false;
            }
        },
        togglePptxAgent() {
            this.use_pptx_agent = !this.use_pptx_agent;
            if (this.use_pptx_agent) {
                this.deepResearch = false;
                this.use_flowcase_bot = false;
                this.use_company_context_addon = false;
                this.use_proposal_agent = false;
                this.use_n8n_agent = false;
                this.use_transcript_agent = false;
            }
        },
        toggleProposalAgent() {
            this.use_proposal_agent = !this.use_proposal_agent;
            if (this.use_proposal_agent) {
                this.deepResearch = false;
                this.use_flowcase_bot = false;
                this.use_company_context_addon = false;
                this.use_pptx_agent = false;
                this.use_n8n_agent = false;
                this.use_transcript_agent = false;
            }
        },
        async toggleN8nAgent() {
            if (!this.use_n8n_agent) {
                // Turning ON — check API key first
                this.n8n_key_checking = true;
                try {
                    const token = localStorage.getItem('token');
                    const resp = await axios.get(`${config.baseURL}/api/v1/user/settings/`, {
                        headers: { Authorization: `Token ${token}` },
                    });
                    this.n8n_api_key_set = resp.data.n8n_api_key_set;
                } catch (e) {
                    this.n8n_api_key_set = false;
                } finally {
                    this.n8n_key_checking = false;
                }

                if (!this.n8n_api_key_set) {
                    // No API key — show red warning in input field
                    this.use_n8n_agent = true;
                    this.inputDisabled = true;
                    this.deepResearch = false;
                    this.use_flowcase_bot = false;
                    this.use_company_context_addon = false;
                    this.use_pptx_agent = false;
                    this.use_proposal_agent = false;
                    this.use_transcript_agent = false;
                    return;
                }

                // API key is set — enable agent
                this.use_n8n_agent = true;
                this.inputDisabled = false;
                this.deepResearch = false;
                this.use_flowcase_bot = false;
                this.use_company_context_addon = false;
                this.use_pptx_agent = false;
                this.use_proposal_agent = false;
                this.use_transcript_agent = false;
            } else {
                // Turning OFF
                this.use_n8n_agent = false;
                this.inputDisabled = false;
            }
        },
        toggleTranscriptAgent() {
            // Don't toggle on — just open the picker. Chip appears only after file selection.
            if (!this.use_transcript_agent) {
                this.dismissFeature('transcript');
                this.$nextTick(() => {
                    this.$refs.audioFileInput.click();
                });
            } else {
                this.onRemoveTranscriptFile();
            }
        },
        onRemoveTranscriptFile() {
            this.transcriptFile = null;
            this.transcriptFilename = '';
            this.use_transcript_agent = false;
        },
        async handleAudioFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            const audioTypes = ['audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/aac'];
            if (!audioTypes.includes(file.type) && !file.name.match(/\.(mp3|m4a|wav|ogg|webm|aac|caf)$/i)) {
                return;
            }

            // Activate agent and show chip
            this.deepResearch = false;
            this.use_flowcase_bot = false;
            this.use_company_context_addon = false;
            this.use_pptx_agent = false;
            this.use_proposal_agent = false;
            this.use_n8n_agent = false;
            this.use_transcript_agent = true;
            this.transcriptFile = file;
            this.transcriptFilename = file.name.length > 25 ? file.name.slice(0, 22) + '...' : file.name;
            event.target.value = '';

            // Immediately upload — show progress in chat
            const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
            const fileHtml = `<div class="file-attachment">
                <div class="file-icon"><span class="mdi mdi-microphone-outline"></span></div>
                <div class="file-info">
                    <span class="file-name">${this.transcriptFilename}</span>
                    <span class="file-type">Audio · ${fileSizeMB} MB</span>
                </div>
            </div>`;
            this.messages.push({ text: fileHtml, from: 'user' });

            this.isTyping = true;
            this.typingText = 'Uploading...';
            this.isUploading = true;
            this.uploadProgress = 0;
            this.isPrintingText = true;
            this.chatScrollToBottom();

            try {
                const token = localStorage.getItem('token');
                const formData = new FormData();
                formData.append('audioFile', file, file.name);

                const res = await axios.post(`${config.baseURL}/api/v1/transcript/upload/`, formData, {
                    headers: { 'Authorization': `Token ${token}` },
                    timeout: 120000,
                    onUploadProgress: (e) => {
                        if (e.total) {
                            this.uploadProgress = Math.round((e.loaded / e.total) * 100);
                        }
                    },
                });

                // Store file_id for later processing
                this.transcriptFileId = res.data.file_id;
                this.transcriptEmail = this.email;

                // Show confirmation prompt immediately
                this.isUploading = false;
                this.uploadProgress = 0;
                this.isTyping = false;
                this.isPrintingText = false;

                const confirmText = `Thanks! I received the file <strong>${this.transcriptFilename}</strong>. ` +
                    `I will send the result to <strong>${this.transcriptEmail}</strong>. ` +
                    `Is that correct and can I start? Or do you want to send it to another email address?`;
                this.messages.push({ text: confirmText, from: 'bot' });
                this.transcriptState = 'awaiting_confirm';
                this.transcriptTask = '';
                this.chatScrollToBottom();

            } catch (e) {
                console.error('[Transcript] Upload failed:', e);
                this.isUploading = false;
                this.uploadProgress = 0;
                this.isTyping = false;
                this.isPrintingText = false;
                this.messages.push({
                    text: `<div class="transcript-error"><span class="mdi mdi-alert-circle"></span> Upload failed: ${e.response?.data?.error || e.message}</div>`,
                    from: 'bot'
                });
                this.onRemoveTranscriptFile();
            }
        },
        /**
         * Handle Share Target: retrieve audio file shared from another app (e.g. Voice Memos).
         * The Service Worker caches the file from the POST, and we retrieve it here via postMessage.
         */
        async handleShareTargetAudio() {
            const params = new URLSearchParams(window.location.search);
            if (params.get('shared') !== 'audio') return;

            // Clean the URL (remove ?shared=audio) without reloading
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);

            // Wait for Service Worker to be ready
            if (!('serviceWorker' in navigator)) return;
            const registration = await navigator.serviceWorker.ready;
            const sw = registration.active;
            if (!sw) return;

            // Request the shared audio file from the Service Worker
            return new Promise((resolve) => {
                const handler = (event) => {
                    if (event.data && event.data.type === 'SHARED_AUDIO_RESULT') {
                        navigator.serviceWorker.removeEventListener('message', handler);
                        const fileData = event.data.file;
                        if (fileData && fileData.blob) {
                            // Convert the blob to a File object
                            const file = new File(
                                [fileData.blob],
                                fileData.fileName || 'recording.m4a',
                                { type: fileData.type || 'audio/mp4' }
                            );
                            // Activate Transcript Agent and set the file
                            this.use_transcript_agent = true;
                            this.deepResearch = false;
                            this.use_flowcase_bot = false;
                            this.use_company_context_addon = false;
                            this.use_pptx_agent = false;
                            this.use_proposal_agent = false;
                            this.use_n8n_agent = false;
                            this.transcriptFile = file;
                            this.transcriptFilename = file.name.length > 25
                                ? file.name.slice(0, 22) + '...'
                                : file.name;
                        }
                        resolve();
                    }
                };
                navigator.serviceWorker.addEventListener('message', handler);
                sw.postMessage({ type: 'GET_SHARED_AUDIO' });

                // Timeout: don't wait forever if SW doesn't respond
                setTimeout(() => {
                    navigator.serviceWorker.removeEventListener('message', handler);
                    resolve();
                }, 3000);
            });
        },
        onRemoveFile() {
            this.fileupload = false;
            this.filename = '';
            this.selectedFile = undefined;
            this.filecontent = '';
            this.fileprocessing = false;
            this.fileerror_message = '';
            this.convertingPdfDone = false;
        },
        onUploadFile() {
            this.$refs.fileInput.click();
        },
        handleFileError(message) {
            this.selectedFile = undefined;
            this.filename = '';
            this.fileupload = false;
            this.filecontent = '';
            this.fileprocessing = false;
            this.fileerror_message = message;
        },
        async handleFileUpload(event) {
            this.fileprocessing = true;
            const file = event.target.files[0];
            this.processFile(file);
        },
        processFile(file) {
            if (!file) return;
            // Workflow Builder accepts any file type
            if (this.use_n8n_agent) {
                this.selectedFile = file;
                this.filename = file.name.length > 25 ? file.name.slice(0, 22) + '...' : file.name;
                this.fileupload = true;
                this.convertingPdfDone = true;
                this.fileprocessing = false;
            } else if (file.type === 'application/pdf') {
                this.selectedFile = file;
                this.filename = file.name.length > 25 ? file.name.slice(0, 22) + '...' : file.name;
                this.fileupload = true;
                this.convertingPdfDone = true;
                this.fileprocessing = false;
            } else {
                this.handleFileError('Please provide a PDF file');
            }
        },
        onDragOver(event) {
            this.isDragging = true;
        },
        onDragLeave(event) {
            this.isDragging = false;
        },
        onDrop(event) {
            this.isDragging = false;
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.fileprocessing = true;
                this.processFile(files[0]);
            }
        },
        async stopTyping() {
            this.isPrintingText = false;
            this.isTyping = false;

            // Celery-Task im Backend abbrechen
            if (this.currentTaskId) {
                try {
                    await axios.post(`${config.baseURL}/api/v1/bot/stop/`, {
                        task_id: this.currentTaskId,
                    }, {
                        headers: { Authorization: `Bearer ${this.token}` },
                    });
                } catch (e) {
                    console.warn('Failed to stop task:', e.message);
                }
                this.currentTaskId = null;
            }
        },
        renderMarkdown(text) {
            let html = marked(text);
            // h1-Ueberschriften zu h2 herabstufen (h1 ist reserviert fuer Seitentitel)
            html = html.replace(/<h1([^>]*)>/g, '<h2$1>').replace(/<\/h1>/g, '</h2>');
            html = html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
            if (html.includes('<table')) {
                html = html.replace('<table', '<div class="table-wrapper"><table class="data-table"');
                html = html.replace('</table>', '</table><button class="exportButton"><span class="mdi mdi-microsoft-excel"></span> Export</button></div>');
            }
            // Handle PPTX download tags (old format with base64)
            const pptxRegex = /<pptx-download data="([^"]+)" filename="([^"]+)">([^<]+)<\/pptx-download>/g;
            html = html.replace(pptxRegex, (match, data, filename, label) => {
                return `<button class="pptxDownloadButton" data-pptx="${data}" data-filename="${filename}"><span class="mdi mdi-file-powerpoint"></span> ${label}</button>`;
            });
            // Handle new PPTX_FILE format [PPTX_FILE:filename.pptx]
            const pptxFileRegex = /\[PPTX_FILE:([^\]]+)\]/g;
            html = html.replace(pptxFileRegex, (match, filename) => {
                return `<button class="pptxDownloadButton pptxFileDownload" data-filename="${filename}"><span class="mdi mdi-file-powerpoint"></span> Download PowerPoint</button>`;
            });
            return html;
        },
        downloadPptx(event) {
            const btn = event.target.closest('.pptxDownloadButton');
            if (btn) {
                // Check if it's a file download (new format) or base64 (old format)
                if (btn.classList.contains('pptxFileDownload')) {
                    this.downloadPptxFile(btn);
                } else {
                    const base64Data = btn.getAttribute('data-pptx');
                    const filename = btn.getAttribute('data-filename') || 'presentation.pptx';

                    // Convert base64 to blob
                    const byteCharacters = atob(base64Data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });

                    // Create download link
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }
            }
        },
        async downloadPptxFile(btn) {
            const filename = btn.getAttribute('data-filename');
            if (!filename) return;

            try {
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No token');

                // Fetch file from backend
                const response = await axios.get(`${config.baseURL}/api/v1/download/pptx/${filename}/`, {
                    headers: { Authorization: `Token ${token}` },
                    responseType: 'blob'
                });

                // Create download link
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error downloading PPTX:', error);
                alert('Download fehlgeschlagen. Bitte versuchen Sie es erneut.');
            }
        },
        async getUser() {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;

                const headers = { Authorization: `Token ${token}` };
                const response = await axios.get(`${config.baseURL}/api/v1/user/me/`, { headers });
                if (response.status === 200) {
                    this.username = response.data.first_name + ' ' + response.data.last_name;
                    this.email = response.data.email;
                }

                const addonsResponse = await axios.get(`${config.baseURL}/api/v1/user/addons/`, { headers });
                if (addonsResponse.status === 200) {
                    this.has_flowcase_addon = addonsResponse.data.has_flowcase_addon;
                    this.has_n8n_addon = addonsResponse.data.has_n8n_addon;
                }
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        },
        chatScrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.scrollContainer;
                if (container) {
                    const bottom = container.querySelector('.bottom');
                    if (bottom) {
                        bottom.scrollIntoView({ behavior: 'smooth' });
                    }
                }
            });
        },
        wait(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        fallbackCopy(text) {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            let ok = false;
            try { ok = document.execCommand('copy'); } catch (e) { /* ignore */ }
            document.body.removeChild(ta);
            return ok;
        },
        /**
         * Transcript Agent: Handles user responses after upload + confirmation prompt.
         * Upload + confirmation happen in handleAudioFileUpload().
         * This method handles: confirm, cancel, change_email, change_task.
         */
        async sendTranscriptMessage() {
            const userText = this.input.trim();

            // Confirmation is already shown after upload — handle user responses
            if (this.transcriptState === 'awaiting_confirm') {
                this.messages.push({ text: this.formatUserText(userText), from: 'user' });
                this.input = '';
                const richEl = this.getRichInputEl();
                if (richEl) { richEl.innerHTML = ''; richEl.style.height = 'auto'; }
                this.isTyping = true;
                this.isPrintingText = true;
                this.typingText = DEFAULT_TYPING_TEXT;
                this.chatScrollToBottom();

                const classifyResponse = await openai_client_call(
                    `You are classifying a user response in a transcript confirmation dialog.\n` +
                    `The user was asked to confirm processing an audio file. Their response: "${userText}"\n\n` +
                    `Classify the response as one of:\n` +
                    `- "confirm" — user agrees (yes, ok, go ahead, bestätigen, ja, passt, etc.)\n` +
                    `- "cancel" — user wants to abort (no, cancel, abbrechen, nein, etc.)\n` +
                    `- "change_email:new@email.com" — user wants a different email (extract the new email)\n` +
                    `- "change_task:new task description" — user wants to change/add a task\n\n` +
                    `Respond with ONLY the classification, nothing else.`
                );

                const classification = classifyResponse.trim().toLowerCase();

                if (classification.startsWith('confirm')) {
                    // Trigger processing — file is already on the server
                    try {
                        const token = localStorage.getItem('token');
                        const submitRes = await axios.post(`${config.baseURL}/api/v1/transcript/`, {
                            file_id: this.transcriptFileId,
                            email: this.transcriptEmail,
                            task: this.transcriptTask || '',
                        }, {
                            headers: {
                                'Authorization': `Token ${token}`,
                                'Content-Type': 'application/json',
                            },
                            timeout: 30000,
                        });

                        const taskId = submitRes.data?.task_id;

                        const successMsg = await openai_client_call(
                            `The user confirmed and the audio transcription job has been submitted successfully. ` +
                            `Generate a short, friendly success message. ` +
                            `IMPORTANT: Respond in the SAME LANGUAGE as: "${userText}". Default to German if unclear.\n` +
                            `Include ONLY:\n` +
                            `- A clear indicator that the transcription has started (e.g. "Transcription started")\n` +
                            `- Results will be sent by email to: ${this.transcriptEmail}\n` +
                            `- The user can keep chatting OR close the browser window — they will receive the result either way\n` +
                            `STRICT RULES:\n` +
                            `- DO NOT mention any duration, time estimate, or how many minutes it takes.\n` +
                            `- DO NOT reference the file size.\n` +
                            `- DO NOT use markdown headers.\n` +
                            `Keep it to 2 short sentences total.`
                        );
                        this.addMessage(successMsg);

                        if (taskId) {
                            this.pollTranscriptTask(taskId, token, userText);
                        }
                    } catch (e) {
                        console.error('[Transcript] Submit failed:', e);
                        const errMsg = e.response?.data?.error || e.message;
                        try {
                            const errorMsg = await openai_client_call(
                                `An error occurred while submitting the audio transcription: "${errMsg}". ` +
                                `Generate a short, friendly error message explaining what went wrong. ` +
                                `Respond in the SAME LANGUAGE as: "${userText}". Default to German if unclear. ` +
                                `Keep it to 2-3 sentences. Do NOT use markdown headers.`
                            );
                            this.addMessage(errorMsg);
                        } catch (_) {
                            this.addMessage(`Error: ${errMsg}`);
                        }
                    }
                    this.resetTranscriptState();

                } else if (classification.startsWith('change_email:')) {
                    const newEmail = classification.replace('change_email:', '').trim();
                    this.transcriptEmail = newEmail;
                    const confirmMsg = await openai_client_call(
                        `The user changed their email to "${newEmail}" for receiving transcript results. ` +
                        `Generate a short confirmation and ask again if everything is correct now. ` +
                        `Respond in the SAME LANGUAGE as: "${userText}". Default to German if unclear. ` +
                        `File: "${this.transcriptFilename}", Task: "${this.transcriptTask || '(transcription only)'}". ` +
                        `Keep it concise. Do NOT use markdown headers.`
                    );
                    this.addMessage(confirmMsg);
                    // Stay in 'awaiting_confirm'

                } else if (classification.startsWith('change_task:')) {
                    const newTask = classification.replace('change_task:', '').trim();
                    this.transcriptTask = newTask;
                    const confirmMsg = await openai_client_call(
                        `The user changed the task for the transcript to: "${newTask}". ` +
                        `Generate a short confirmation and ask again if everything is correct now. ` +
                        `Respond in the SAME LANGUAGE as: "${userText}". Default to German if unclear. ` +
                        `File: "${this.transcriptFilename}", Email: "${this.transcriptEmail}". ` +
                        `Keep it concise. Do NOT use markdown headers.`
                    );
                    this.addMessage(confirmMsg);
                    // Stay in 'awaiting_confirm'

                } else {
                    // cancel or unrecognized
                    const cancelMsg = await openai_client_call(
                        `The user cancelled or declined the audio transcription. ` +
                        `Generate a short, friendly cancellation message. ` +
                        `Respond in the SAME LANGUAGE as: "${userText}". Default to German if unclear. ` +
                        `Keep it to 1-2 sentences. Do NOT use markdown headers.`
                    );
                    this.addMessage(cancelMsg);
                    this.resetTranscriptState();
                }
                return;
            }
        },
        /**
         * Background polling for transcript task errors.
         * Only shows a message if the task fails — success is silent (email delivery).
         */
        async pollTranscriptTask(taskId, token, userLang) {
            const headers = { headers: { Authorization: `Token ${token}` } };
            const botUrl = `${config.baseURL}/api/v1/bot/`;
            const maxPolls = 60; // ~10 minutes at 10s intervals

            for (let i = 0; i < maxPolls; i++) {
                await new Promise(r => setTimeout(r, 10000)); // 10s interval
                try {
                    const res = await axios.get(`${botUrl}?task_id=${encodeURIComponent(taskId)}`, headers);
                    const { state, error } = res.data || {};

                    if (state === 'SUCCESS') return; // Email sent, all good
                    if (state === 'FAILURE' || res.status === 500) {
                        const errDetail = error || res.data?.error || 'Unknown error';
                        try {
                            const errorMsg = await openai_client_call(
                                `The audio transcription that was submitted earlier has failed in the background. ` +
                                `Error: "${errDetail}". ` +
                                `Generate a short message informing the user about this failure. ` +
                                `Respond in the SAME LANGUAGE as: "${userLang}". Default to German if unclear. ` +
                                `Keep it to 2-3 sentences. Do NOT use markdown headers.`
                            );
                            this.messages.push({ text: this.renderMarkdown(errorMsg), from: 'bot' });
                        } catch (_) {
                            this.messages.push({ text: `Transcript error: ${errDetail}`, from: 'bot' });
                        }
                        this.chatScrollToBottom();
                        return;
                    }
                    // PENDING / PROGRESS — keep polling
                } catch (e) {
                    // Network error — stop polling silently
                    console.warn('[Transcript Poll] Network error, stopping:', e.message);
                    return;
                }
            }
        },
        resetTranscriptState() {
            this.transcriptState = null;
            this.transcriptTask = '';
            this.transcriptEmail = '';
            this.transcriptFileId = null;
            this.use_transcript_agent = false;
            this.onRemoveTranscriptFile();
        },
        async sendMessage() {
            if (this.isPrintingText) return;

            // Transcript Agent: multi-step conversation flow
            if (this.transcriptState === 'awaiting_confirm') {
                if (this.input.trim() === '') return;
                await this.sendTranscriptMessage();
                return;
            }

            if (this.input.trim() === '') return;

            while (this.fileupload && this.fileprocessing) {
                await this.wait(500);
            }

            // Kombiniere Datei und Text in einer einzigen User-Bubble
            if (this.fileupload) {
                const fileHtml = `<div class="file-attachment">
                    <div class="file-icon"><span class="mdi mdi-file-pdf-box"></span></div>
                    <div class="file-info">
                        <span class="file-name">${this.filename}</span>
                        <span class="file-type">PDF Document</span>
                    </div>
                </div>`;
                const messageHtml = this.input ? `<div class="user-text">${this.formatUserText(this.input)}</div>` : '';
                this.messages.push({
                    text: fileHtml + messageHtml,
                    from: 'user'
                });
            } else {
                this.messages.push({ text: this.formatUserText(this.input), from: 'user' });
            }

            const url = `${config.baseURL}/api/v1/bot/`;
            let chat_id = this.selectedChatId || 1;

            try {
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No token');

                const headers = { headers: { Authorization: `Token ${token}` } };
                const formData = new FormData();
                formData.append('user_message', this.input);
                formData.append('chat_id', chat_id);
                formData.append('email', this.email);
                formData.append('new_thread', this.new_thread);

                if (this.new_thread) {
                    this.thread_id = crypto.randomUUID().replace(/-/g, '');
                }
                formData.append('thread_id', this.thread_id);
                formData.append('model_selection', this.models);
                formData.append('deepResearch', this.deepResearch);
                formData.append('use_flowcase_bot', this.use_flowcase_bot);
                formData.append('use_company_context_addon', this.use_company_context_addon);
                formData.append('use_pptx_agent', this.use_pptx_agent);
                formData.append('use_proposal_agent', this.use_proposal_agent);
                formData.append('use_n8n_agent', this.use_n8n_agent);

                if (this.selectedFile) {
                    formData.append('file', this.selectedFile);
                    // Bei PowerPoint Agent: PDF nur für PPTX-Erstellung, NICHT als Chat-Kontext
                    if (!this.use_pptx_agent) {
                        this.pdf_context = true;
                    }
                }
                formData.append('file_context', this.pdf_context);

                this.new_thread = false;
                this.isTyping = true;
                this.isPrintingText = true;
                this.chatScrollToBottom();
                this.input = '';

                // Reset rich input field
                const richEl = this.getRichInputEl();
                if (richEl) {
                    richEl.innerHTML = '';
                    richEl.style.height = 'auto';
                }

                // Show upload progress when a file is attached
                if (this.selectedFile) {
                    this.typingText = 'Uploading...';
                    this.isUploading = true;
                    this.uploadProgress = 0;
                } else {
                    this.typingText = DEFAULT_TYPING_TEXT;
                }

                this.onRemoveFile();

                const startRes = await axios.post(url, formData, {
                    ...headers,
                    onUploadProgress: (e) => {
                        if (e.total && this.isUploading) {
                            this.uploadProgress = Math.round((e.loaded / e.total) * 100);
                        }
                        if (this.uploadProgress >= 100) {
                            this.isUploading = false;
                            this.typingText = DEFAULT_TYPING_TEXT;
                        }
                    },
                });
                const taskId = startRes.data?.task_id;
                if (!taskId) throw new Error('No task_id');
                this.currentTaskId = taskId;

                while (true) {
                    const statusRes = await axios.get(`${url}?task_id=${encodeURIComponent(taskId)}`, headers);
                    const { state, result } = statusRes.data || {};

                    if (state?.startsWith?.('PROGRESS_') || state === 'PROGRESS' || state === 'STARTED') {
                        // If backend sends meta.detail (live agent action), show that
                        const detail = statusRes.data?.result?.detail || result?.detail;
                        if (detail) {
                            this.typingText = detail;
                        } else {
                            const stateMap = {
                                'PROGRESS_WEBSEARCH': WEBSEARCH_TYPING_TEXT,
                                'PROGRESS_PDF': PDF_TYPING_TEXT,
                                'PROGRESS_DEEPRESEARCH': DEEPRESEARCH_TYPING_TEXT,
                                'PROGRESS_SUMMARY': SUMMARY_TYPING_TEXT,
                                'PROGRESS_REFERENCESBOT': PROGRESS_REFERENCESBOT,
                                'PROGRESS_COMPANYCONTEXT': PROGRESS_COMPANYCONTEXT,
                                'PROGRESS_PPTX': PROGRESS_PPTX,
                                'PROGRESS_N8N': PROGRESS_N8N,
                                'PROGRESS_AGENT_LOOP': 'Thinking...',
                            };
                            this.typingText = stateMap[state] || DEFAULT_TYPING_TEXT;
                        }

                        // Agent-Chip sofort aktivieren wenn Backend an Sub-Agent dispatcht
                        const progressAgentMap = {
                            'PROGRESS_PPTX': 'pptx',
                            'PROGRESS_DEEPRESEARCH': 'deep_research',
                            'PROGRESS_REFERENCESBOT': 'flowcase',
                        };
                        if (progressAgentMap[state]) {
                            this._activateAgent(progressAgentMap[state]);
                        }

                        this.chatScrollToBottom();
                    }

                    // Abbruch wenn User Stop gedrueckt hat
                    if (!this.isTyping) {
                        this.currentTaskId = null;
                        return;
                    }

                    if (statusRes.status === 200 && state === 'SUCCESS') {
                        this.currentTaskId = null;
                        this.pdf_context = result.file_context;

                        // Backend kann den PowerPoint-Agent-Chip steuern (z.B. nach
                        // Bestaetigung des Email-Versands → Chip wird ausgeschaltet).
                        if (typeof result.use_pptx_agent === 'boolean') {
                            this.use_pptx_agent = result.use_pptx_agent;
                        }

                        // Dynamisches Agent-Switching basierend auf Backend-Erkennung
                        const switchAgent = result.meta?.switch_agent;
                        if (switchAgent) {
                            this._activateAgent(switchAgent);
                        }

                        this.addMessage(result.result);
                        this.isTyping = false;
                        this.isPrintingText = false;
                        this.chatScrollToBottom();
                        return;
                    }

                    if (state === 'FAILURE' || state === 'REVOKED') {
                        this.currentTaskId = null;
                        if (state === 'REVOKED') {
                            this.addMessage('Request was stopped.');
                        } else {
                            this.addMessage('An error occurred. Please try again.');
                        }
                        this.isTyping = false;
                        this.isPrintingText = false;
                        return;
                    }

                    await this.wait(1000);
                }
            } catch (error) {
                this.addMessage('An error occurred. Please try again.');
                this.isTyping = false;
                this.isPrintingText = false;
                this.isUploading = false;
                this.uploadProgress = 0;
                console.error('Error:', error);
            }
        },
        exportToExcel(event) {
            const btn = event.target.closest('.exportButton');
            if (btn) {
                const table = btn.closest('.table-wrapper')?.querySelector('.data-table');
                if (table) {
                    const workbook = XLSX.utils.table_to_book(table, { sheet: "Sheet1" });
                    XLSX.writeFile(workbook, 'export.xlsx');
                }
            }
        },
        addMessage(message) {
            this.messages.push({ text: this.renderMarkdown(message), from: 'bot' });
            this.isTyping = false;
            this.isPrintingText = false;
        },
    },
    watch: {
        messages: {
            handler() {
                this.$nextTick(() => {
                    this.chatScrollToBottom();
                    if (this.$refs.scrollContainer) {
                        const exportButtons = this.$refs.scrollContainer.querySelectorAll('.exportButton');
                        exportButtons.forEach(btn => btn.addEventListener('click', this.exportToExcel));

                        const pptxButtons = this.$refs.scrollContainer.querySelectorAll('.pptxDownloadButton');
                        pptxButtons.forEach(btn => btn.addEventListener('click', this.downloadPptx));
                    }
                });
            },
            deep: true,
        },
    },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* ============================================
   Light Theme Chat Interface
   ============================================ */

/* Base App Container */
.chat-app {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 64px);
    width: 100%;
    background:
        radial-gradient(ellipse 80% 70% at 10% 15%, rgba(0, 109, 148, 0.08) 0%, transparent 65%),
        radial-gradient(ellipse 70% 60% at 90% 20%, rgba(211, 82, 48, 0.07) 0%, transparent 65%),
        radial-gradient(ellipse 85% 75% at 75% 85%, rgba(0, 109, 148, 0.06) 0%, transparent 65%),
        radial-gradient(ellipse 60% 50% at 25% 80%, rgba(211, 82, 48, 0.05) 0%, transparent 65%),
        #f4f5f6;
    color: #1a1a1a;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    bottom: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow: hidden;
}

.chat-app.mobile {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;
    height: auto;
    background:
        radial-gradient(ellipse 80% 70% at 10% 15%, rgba(0, 109, 148, 0.08) 0%, transparent 65%),
        radial-gradient(ellipse 70% 60% at 90% 20%, rgba(211, 82, 48, 0.07) 0%, transparent 65%),
        radial-gradient(ellipse 85% 75% at 75% 85%, rgba(0, 109, 148, 0.06) 0%, transparent 65%),
        radial-gradient(ellipse 60% 50% at 25% 80%, rgba(211, 82, 48, 0.05) 0%, transparent 65%),
        #f4f5f6;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Drag & Drop Overlay */
.chat-app.drag-over::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(14, 165, 233, 0.06);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    border: 3px dashed rgba(14, 165, 233, 0.5);
    border-radius: 12px;
    z-index: 1000;
    pointer-events: none;
}

.chat-app.drag-over::after {
    content: 'Drop PDF here';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(14, 165, 233, 0.8);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    color: white;
    padding: 16px 32px;
    border-radius: 16px;
    font-size: 18px;
    font-weight: 500;
    z-index: 1001;
    pointer-events: none;
    box-shadow: 0 8px 32px rgba(14, 165, 233, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Welcome Screen */
.welcome-screen {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 180px;
}

.welcome-content {
    text-align: center;
    padding: 40px;
}

.welcome-avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(14, 165, 233, 0.7) 0%, rgba(2, 132, 199, 0.7) 100%);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    color: white;
    font-size: 36px;
    box-shadow: 0 8px 32px rgba(14, 165, 233, 0.2);
}

.welcome-title {
    font-size: 32px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 12px;
}

.welcome-subtitle {
    font-size: 18px;
    color: #64748b;
    margin: 0;
    font-weight: 400;
}

.chat-app.mobile .welcome-screen {
    flex: 1;
    padding-bottom: 0;
}

.chat-app.mobile .welcome-avatar {
    width: 60px;
    height: 60px;
    font-size: 30px;
    margin-bottom: 20px;
}

.chat-app.mobile .welcome-title {
    font-size: 26px;
}

.chat-app.mobile .welcome-subtitle {
    font-size: 16px;
}

/* Main Chat Area */
.chat-main {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    padding-bottom: 180px;
    overflow: hidden;
}

/* Messages Area */
.messages-area {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    min-width: 100%;
    overscroll-behavior: contain;
}

.messages-inner {
    max-width: 1200px;
    min-width: min(1200px, 100%);
    width: 100%;
    margin: 0 auto;
    padding: 24px 40px 0;
    box-sizing: border-box;
}

.messages-inner .bottom {
    height: 40px;
    margin: 0;
    padding: 0;
}

.chat-app.mobile .messages-area {
    flex: 1 1 0;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    max-height: 100%;
    -webkit-overflow-scrolling: touch;
}

.chat-app.mobile .messages-inner {
    max-width: 100%;
    padding: 16px 12px;
    margin: 0;
    padding-bottom: 24px;
}

.chat-app.mobile .messages-inner .bottom {
    display: none;
}

.chat-app.mobile .message-row {
    padding: 6px 0;
}

.chat-app.mobile .message-row.user .message-content {
    max-width: 90%;
}

.chat-app.mobile .message-row.user .message-text {
    margin-right: 4px;
    word-break: normal;
    overflow-wrap: break-word;
    white-space: pre-wrap;
}

.chat-app.mobile .message-row.assistant .message-text {
    max-width: 100%;
}

.chat-app.mobile .avatar {
    width: 28px;
    height: 28px;
    min-width: 28px;
    font-size: 16px;
}

.chat-app.mobile .message-content {
    gap: 8px;
}

/* Message Row */
.message-row {
    padding: 8px 0;
    display: flex;
}

.message-row:first-child {
    padding-top: 0;
}

.message-row:last-of-type {
    padding-bottom: 0;
}

.message-row.user {
    justify-content: flex-end;
}

.message-row.assistant {
    justify-content: flex-start;
}

.message-content {
    display: flex;
    gap: 12px;
    max-width: 100%;
    min-width: 0;
    text-align: left;
    overflow: hidden;
}

.message-row.user .message-content {
    flex-direction: row-reverse;
    text-align: left;
    max-width: 75%;
    margin-left: auto;
}

/* Avatar */
.avatar {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(14, 165, 233, 0.7) 0%, rgba(2, 132, 199, 0.7) 100%);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
    box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
}

/* Message Text */
.message-text {
    line-height: 1.5;
    font-size: 15px;
    color: #1a1a1a;
    text-align: left;
    overflow-wrap: break-word;
    word-break: break-word;
    min-width: 0;
}

.message-row.user .message-text {
    background: rgba(14, 165, 233, 0.75);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    color: white;
    padding: 12px 18px;
    border-radius: 20px 20px 4px 20px;
    text-align: left;
    margin-right: 12px;
    word-break: normal;
    overflow-wrap: break-word;
    white-space: pre-wrap;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 16px rgba(14, 165, 233, 0.15);
}

.message-row.assistant .message-text {
    padding: 4px 0;
    text-align: left;
    max-width: 100%;
    min-width: 0;
    overflow-wrap: break-word;
    word-break: break-word;
}

/* Typing Indicator */
.typing-indicator {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    color: #64748b;
    font-size: 14px;
}

.typing-text {
    font-weight: 500;
}

.upload-ring {
    flex-shrink: 0;
}

.typing-dots {
    display: flex;
    gap: 4px;
}

.typing-dots span {
    width: 6px;
    height: 6px;
    background: #0ea5e9;
    border-radius: 50%;
    animation: bounce 1.4s ease-in-out infinite;
}

.typing-dots span:nth-child(2) { animation-delay: 0.2s; }
.typing-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
    40% { transform: translateY(-6px); opacity: 1; }
}

/* Input Area */
.input-area {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 24px;
    background: linear-gradient(to top, rgba(255, 255, 255, 0.65) 0%, rgba(255, 255, 255, 0.35) 70%, transparent 100%);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-top: none;
    z-index: 100;
}

.mobile-input-area {
    padding: 12px 8px;
    flex-shrink: 0;
    background: linear-gradient(to top, rgba(255, 255, 255, 0.65) 0%, rgba(255, 255, 255, 0.35) 70%, transparent 100%);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-top: none;
}

.input-wrapper {
    max-width: 1200px;
    margin: 0 auto;
}

.chat-app.mobile .input-wrapper,
.mobile-input-area .input-wrapper {
    max-width: 100%;
}

/* Input Toolbar */
.input-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.toolbar-model-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 20px;
    color: #1e293b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.toolbar-model-btn:hover {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(255, 255, 255, 0.6);
}

.toolbar-model-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.toolbar-model-btn .mdi {
    font-size: 16px;
    color: #64748b;
}

.toolbar-model-btn .mdi-chevron-down {
    font-size: 18px;
    margin-left: -2px;
}

.toolbar-model-btn .model-version {
    color: #64748b;
    font-weight: 400;
    font-size: 12px;
}

.chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: rgba(241, 245, 249, 0.6);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 20px;
    font-size: 13px;
    color: #475569;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.chip .mdi {
    color: #0ea5e9;
}

.chip.file {
    background: rgba(236, 253, 245, 0.6);
    border-color: rgba(167, 243, 208, 0.5);
    color: #047857;
}

.chip.file .mdi {
    color: #10b981;
}

.chip.file.pptx-context {
    background: rgba(254, 243, 199, 0.6);
    border-color: rgba(252, 211, 77, 0.5);
    color: #92400e;
}

.chip.file.pptx-context .mdi {
    color: #f59e0b;
}

.chip button {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 0;
    margin-left: 4px;
    transition: color 0.15s;
}

.chip button:hover {
    color: #1e293b;
}

/* Input Container */
.input-container {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 24px;
    padding: 8px 8px 8px 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: border-color 0.2s, box-shadow 0.2s;
}

.input-container:focus-within {
    border-color: rgba(14, 165, 233, 0.5);
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.12), 0 4px 24px rgba(0, 0, 0, 0.08);
}

.input-container textarea,
.input-container .rich-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #1a1a1a;
    font-size: 15px;
    line-height: 1.5;
    resize: none;
    max-height: 200px;
    padding: 4px 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    overflow-y: auto;
    min-height: 24px;
    white-space: pre-wrap;
    word-break: break-word;
    text-align: left;
}

.input-container .rich-input:empty::before {
    content: attr(data-placeholder);
    color: #94a3b8;
    pointer-events: none;
}

.input-container .rich-input.disabled {
    pointer-events: none;
}

.input-container .rich-input.input-error:empty::before {
    color: #e53935;
    font-weight: 500;
}

.input-container .rich-input ol,
.input-container .rich-input ul {
    margin: 2px 0;
    padding-left: 24px;
}

.input-container .rich-input li {
    margin-bottom: 1px;
}

.input-container .rich-input ol {
    list-style-type: decimal;
}

.input-container .rich-input ul {
    list-style-type: disc;
}

.input-container .rich-input .list-prefix {
    display: none;
}

.input-container textarea::placeholder {
    color: #94a3b8;
}

.input-container textarea:disabled {
    opacity: 0.5;
}

/* Icon Buttons */
.icon-btn {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    min-height: 36px !important;
    max-width: 36px !important;
    max-height: 36px !important;
    border-radius: 50% !important;
    border: none;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    font-size: 20px;
    flex-shrink: 0;
    flex-grow: 0;
    padding: 0 !important;
    box-sizing: border-box;
}

.icon-btn:hover {
    background: #f1f5f9;
    color: #1e293b;
}

.icon-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

/* New feature badge (blue dot) */
.plus-btn {
    position: relative;
}

.new-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 8px;
    height: 8px;
    background: #3b82f6;
    border-radius: 50%;
    pointer-events: none;
}

.new-badge.menu-badge {
    position: static;
    display: inline-block;
    margin-left: 6px;
    flex-shrink: 0;
}

.send-btn {
    background: rgba(14, 165, 233, 0.8);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: white;
    box-shadow: 0 2px 8px rgba(14, 165, 233, 0.2);
}

.send-btn:hover:not(:disabled) {
    background: rgba(2, 132, 199, 0.9);
}

.send-btn:disabled {
    background: rgba(226, 232, 240, 0.6);
    color: #94a3b8;
    backdrop-filter: none;
    box-shadow: none;
}

.stop-btn {
    background: rgba(241, 245, 249, 0.6);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: #64748b;
}

.stop-btn:hover {
    background: rgba(226, 232, 240, 0.7);
    color: #475569;
}

/* Input Footer */
.input-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 12px;
    color: #94a3b8;
}

.input-footer .separator {
    color: #cbd5e1;
}

.input-footer .link {
    color: #64748b;
    cursor: pointer;
    transition: color 0.15s;
}

.input-footer .link:hover {
    color: #0ea5e9;
}

/* Dropdown Menu */
.dropdown-menu {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 16px;
    padding: 8px 0;
    min-width: 220px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    background: transparent;
    border: none;
    color: #1e293b;
    font-size: 14px;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
}

.dropdown-item:hover {
    background: rgba(248, 250, 252, 0.6);
}

.dropdown-item.active {
    color: #0ea5e9;
    background: rgba(240, 249, 255, 0.6);
}

.dropdown-item .mdi {
    font-size: 18px;
    color: #64748b;
}

.dropdown-item.active .mdi {
    color: #0ea5e9;
}

.dropdown-item .mdi-check {
    margin-left: auto;
    color: #0ea5e9;
}

.dropdown-section-header {
    padding: 6px 16px 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(100, 116, 139, 0.8);
    border-top: 1px solid rgba(226, 232, 240, 0.5);
    margin-top: 4px;
    pointer-events: none;
}
.dropdown-section-header.first {
    border-top: none;
    margin-top: 0;
}

.dropdown-divider {
    height: 1px;
    background: rgba(226, 232, 240, 0.5);
    margin: 8px 0;
}

/* Model Menu */
.model-menu {
    min-width: 280px;
}

.model-menu .dropdown-item {
    padding: 14px 16px;
}

.model-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
}

.model-header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.model-name {
    font-weight: 500;
    color: #1e293b;
}

.model-version-tag {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
}

.dropdown-item.active .model-version-tag {
    background: #e0f2fe;
    color: #0284c7;
}

.model-desc {
    font-size: 12px;
    color: #64748b;
}

/* Disclaimer Dialog */
.disclaimer-dialog {
    background: #ffffff !important;
    color: #1e293b;
    border-radius: 20px !important;
    overflow: hidden;
}

.disclaimer-dialog.mobile {
    border-radius: 0 !important;
}

.disclaimer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
}

.disclaimer-header h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    color: #1e293b;
}

.close-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
}

.close-btn:hover {
    background: #e2e8f0;
    color: #1e293b;
}

.disclaimer-body {
    padding: 24px;
    font-size: 14px;
    line-height: 1.5;
    max-height: 60vh;
    overflow-y: auto;
}

.disclaimer-body .lang-note {
    text-align: center;
    color: #94a3b8;
    margin-bottom: 20px;
    font-size: 13px;
}

.disclaimer-body h3 {
    font-size: 14px;
    font-weight: 600;
    margin: 20px 0 8px;
    color: #1e293b;
}

.disclaimer-body h3:first-of-type {
    margin-top: 0;
}

.disclaimer-body ul {
    margin: 0 0 0 20px;
    padding: 0;
    color: #475569;
}

.disclaimer-body li {
    margin-bottom: 6px;
}

.disclaimer-body hr {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 24px 0;
}

.disclaimer-body .contact {
    text-align: center;
    color: #64748b;
    margin-top: 20px;
}

.disclaimer-body .contact-block {
    background: #f8fafc;
    padding: 16px;
    border-radius: 8px;
    color: #475569;
    line-height: 1.6;
    margin: 12px 0;
}

.disclaimer-body .agreement-note {
    text-align: center;
    color: #1e293b;
    margin: 20px 0 0;
    padding: 16px;
    background: #f0f9ff;
    border-radius: 8px;
    border-left: 4px solid #0ea5e9;
}

.disclaimer-footer {
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
}

.primary-btn {
    width: 100%;
    padding: 14px 24px;
    background: #0ea5e9;
    border: none;
    border-radius: 12px;
    color: white;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
}

.primary-btn:hover {
    background: #0284c7;
}

/* Loading Overlay */
.loading-overlay {
    position: fixed;
    inset: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

/* Scrollbar */
.messages-area::-webkit-scrollbar {
    width: 8px;
}

.messages-area::-webkit-scrollbar-track {
    background: transparent;
}

.messages-area::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 4px;
}

.messages-area::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.5);
}

/* Message Content Styles */
:deep(.message-text) {
    white-space: normal;
    word-wrap: break-word;

    h1, h2, h3 {
        margin: 20px 0 8px;
        font-weight: 600;
        color: #1e293b;
        line-height: 1.3;
    }
    h1:first-child, h2:first-child, h3:first-child {
        margin-top: 0;
    }
    h1 { font-size: 1.2em !important; }
    h2 { font-size: 1.1em !important; }
    h3 { font-size: 1em !important; }

    p {
        margin: 14px 0;
        line-height: 1.7;
    }
    p:first-child {
        margin-top: 0;
    }
    p:last-child {
        margin-bottom: 0;
    }

    ul, ol {
        margin: 12px 0 12px 24px !important;
        padding: 0 0 0 16px !important;
        line-height: 1.6;
        list-style-position: outside !important;
    }

    ul {
        list-style-type: disc !important;
    }

    ol {
        list-style-type: decimal !important;
    }

    li {
        margin: 4px 0;
        padding-left: 4px;
        display: list-item !important;
    }

    li p {
        margin: 4px 0;
        display: inline;
    }

    ul ul, ol ol, ul ol, ol ul {
        margin: 4px 0 4px 20px !important;
    }

    a {
        color: #0ea5e9;
        text-decoration: none;
    }

    a:hover {
        text-decoration: underline;
    }

    code {
        background: #f1f5f9;
        color: #e11d48;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 13px;
        font-family: 'SF Mono', Monaco, Consolas, monospace;
    }

    pre {
        background: #1e293b;
        color: #e2e8f0;
        padding: 16px;
        border-radius: 12px;
        overflow-x: auto;
        margin: 20px 0;
        line-height: 1.5;
        white-space: pre;
    }

    pre code {
        background: transparent;
        color: inherit;
        padding: 0;
        white-space: pre;
    }

    blockquote {
        border-left: 3px solid #0ea5e9;
        margin: 20px 0;
        padding: 12px 20px;
        background: #f8fafc;
        border-radius: 0 12px 12px 0;
        color: #475569;
    }

    hr {
        border: none;
        border-top: 1px solid #e5e7eb;
        margin: 12px 0;
        opacity: 0.5;
    }

    strong, b {
        font-weight: 600;
        color: #1e293b;
    }

    em, i {
        font-style: italic;
    }

    .table-wrapper {
        overflow-x: auto;
        margin: 20px 0;
    }

    .data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
        border-radius: 8px;
        overflow: hidden;
    }

    .data-table th,
    .data-table td {
        padding: 14px 20px;
        text-align: left;
        border-bottom: 1px solid #e2e8f0;
        white-space: nowrap;
    }

    .data-table th {
        background: #0ea5e9;
        color: white;
        font-weight: 500;
        padding: 16px 20px;
    }

    .data-table tr:hover {
        background: #f8fafc;
    }

    .data-table td:first-child,
    .data-table th:first-child {
        padding-left: 24px;
    }

    .data-table td:last-child,
    .data-table th:last-child {
        padding-right: 24px;
    }

    .exportButton {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-top: 12px;
        padding: 10px 18px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        color: #475569;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s;
    }

    .exportButton:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
    }

    .exportButton .mdi {
        color: #10b981;
        font-size: 18px;
    }

    .pptxDownloadButton {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-top: 16px;
        padding: 12px 24px;
        background: linear-gradient(135deg, #d35230 0%, #b7472a 100%);
        border: none;
        border-radius: 10px;
        color: white;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s;
        box-shadow: 0 2px 8px rgba(211, 82, 48, 0.3);
    }

    .pptxDownloadButton:hover {
        background: linear-gradient(135deg, #b7472a 0%, #9c3d24 100%);
        box-shadow: 0 4px 12px rgba(211, 82, 48, 0.4);
        transform: translateY(-1px);
    }

    .pptxDownloadButton .mdi {
        font-size: 20px;
    }

    .file-msg {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .file-msg .mdi {
        font-size: 20px;
    }

    /* File Attachment in User Message */
    .file-attachment {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 12px;
        margin-bottom: 10px;
        border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .file-attachment .file-icon {
        width: 40px;
        height: 40px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .file-attachment .file-icon .mdi {
        font-size: 22px;
        color: #ffffff;
    }

    .file-attachment .file-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .file-attachment .file-name {
        font-size: 14px;
        font-weight: 500;
        color: #ffffff;
    }

    .file-attachment .file-type {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.7);
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .user-text {
        margin-top: 2px;
    }

    /* Transcript Upload Status Messages */
    .transcript-uploaded {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #22c55e;
        font-size: 13px;
        font-weight: 500;
    }

    .transcript-uploaded .mdi {
        font-size: 16px;
    }

    .transcript-error {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #ef4444;
        font-size: 13px;
        font-weight: 500;
    }

    .transcript-error .mdi {
        font-size: 16px;
    }
}
</style>

<style>
/* Unscoped: prevent overflow from long words, URLs, inline code */
.message-text {
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    min-width: 0 !important;
}

.message-text a {
    word-break: break-all !important;
}

.message-text code {
    word-break: break-all !important;
}

.message-text pre {
    max-width: 100% !important;
    overflow-x: auto !important;
}

.message-text img {
    max-width: 100% !important;
    height: auto !important;
}

/* Unscoped styles for v-html content - Lists */
.message-text p {
    margin: 14px 0 !important;
    line-height: 1.7 !important;
}

.message-text p:first-child {
    margin-top: 0 !important;
}

.message-text p:last-child {
    margin-bottom: 0 !important;
}

.message-text ul,
.message-text ol {
    margin: 14px 0 14px 0 !important;
    padding-left: 40px !important;
    line-height: 1.7 !important;
}

.message-text ul {
    list-style-type: disc !important;
}

.message-text ol {
    list-style-type: decimal !important;
}

.message-text li {
    margin: 4px 0 !important;
    display: list-item !important;
    list-style-position: outside !important;
}

.message-text ul ul,
.message-text ol ol,
.message-text ul ol,
.message-text ol ul {
    margin: 4px 0 4px 0 !important;
    padding-left: 24px !important;
}

.message-text hr {
    border: none !important;
    border-top: 1px solid #e5e7eb !important;
    margin: 8px 0 !important;
    opacity: 0.6 !important;
    height: 0 !important;
}

.message-text .exportButton,
.message-row .message-text .exportButton,
.chat-app .exportButton,
button.exportButton {
    background: rgba(255, 255, 255, 0.5) !important;
    background-color: rgba(255, 255, 255, 0.5) !important;
    color: #475569 !important;
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
}

.message-text .table-wrapper,
.chat-app .table-wrapper {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
}

.message-text .exportButton,
.chat-app .exportButton,
button.exportButton {
    background: rgba(255, 255, 255, 0.5) !important;
    background-color: rgba(255, 255, 255, 0.5) !important;
    backdrop-filter: blur(8px) !important;
    -webkit-backdrop-filter: blur(8px) !important;
    color: #475569 !important;
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    margin-top: 20px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

.message-text .exportButton:hover,
.chat-app .exportButton:hover,
button.exportButton:hover {
    background: rgba(255, 255, 255, 0.7) !important;
    background-color: rgba(255, 255, 255, 0.7) !important;
}

/* PowerPoint Download Button */
.message-text .pptxDownloadButton,
.chat-app .pptxDownloadButton,
button.pptxDownloadButton {
    display: inline-flex !important;
    align-items: center !important;
    gap: 8px !important;
    margin-top: 16px !important;
    padding: 12px 24px !important;
    background: linear-gradient(135deg, rgba(211, 82, 48, 0.8) 0%, rgba(183, 71, 42, 0.8) 100%) !important;
    backdrop-filter: blur(8px) !important;
    -webkit-backdrop-filter: blur(8px) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 12px !important;
    color: white !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: all 0.15s !important;
    box-shadow: 0 4px 16px rgba(211, 82, 48, 0.25) !important;
}

.message-text .pptxDownloadButton:hover,
.chat-app .pptxDownloadButton:hover,
button.pptxDownloadButton:hover {
    background: linear-gradient(135deg, rgba(183, 71, 42, 0.9) 0%, rgba(156, 61, 36, 0.9) 100%) !important;
    box-shadow: 0 6px 20px rgba(211, 82, 48, 0.35) !important;
}

.message-text .pptxDownloadButton .mdi,
.chat-app .pptxDownloadButton .mdi {
    font-size: 20px !important;
}

/* Tabellen-Styles für v-html */
.message-text .table-wrapper {
    overflow-x: auto !important;
    margin: 20px 0 !important;
}

.message-text .data-table,
.message-text table {
    width: 100% !important;
    border-collapse: collapse !important;
    font-size: 14px !important;
    border-radius: 8px !important;
}

.message-text .data-table th,
.message-text .data-table td,
.message-text table th,
.message-text table td {
    padding: 16px 24px !important;
    text-align: left !important;
    border-bottom: 1px solid #e2e8f0 !important;
}

.message-text .data-table th,
.message-text table th {
    background: rgba(14, 165, 233, 0.75) !important;
    backdrop-filter: blur(8px) !important;
    -webkit-backdrop-filter: blur(8px) !important;
    color: white !important;
    font-weight: 500 !important;
    padding: 18px 24px !important;
}

.message-text .data-table tr:hover,
.message-text table tr:hover {
    background: rgba(248, 250, 252, 0.5) !important;
}

/* File Attachment Styles for v-html (unscoped) */
.message-text .file-attachment,
.message-row.user .file-attachment {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    padding: 12px 14px !important;
    background: rgba(255, 255, 255, 0.15) !important;
    backdrop-filter: blur(8px) !important;
    -webkit-backdrop-filter: blur(8px) !important;
    border-radius: 12px !important;
    margin-bottom: 10px !important;
    border: 1px solid rgba(255, 255, 255, 0.25) !important;
}

.message-text .file-attachment .file-icon,
.message-row.user .file-attachment .file-icon {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    background: rgba(255, 255, 255, 0.2) !important;
    border-radius: 8px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.message-text .file-attachment .file-icon .mdi,
.message-row.user .file-attachment .file-icon .mdi {
    font-size: 22px !important;
    color: #ffffff !important;
}

.message-text .file-attachment .file-info,
.message-row.user .file-attachment .file-info {
    display: flex !important;
    flex-direction: column !important;
    gap: 2px !important;
}

.message-text .file-attachment .file-name,
.message-row.user .file-attachment .file-name {
    font-size: 14px !important;
    font-weight: 500 !important;
    color: #ffffff !important;
}

.message-text .file-attachment .file-type,
.message-row.user .file-attachment .file-type {
    font-size: 11px !important;
    color: rgba(255, 255, 255, 0.7) !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
}

.message-text .user-text,
.message-row.user .user-text {
    margin-top: 2px !important;
}

/* Lists inside user bubbles */
.message-row.user .message-text ol,
.message-row.user .message-text ul {
    margin: 4px 0;
    padding-left: 22px;
}

.message-row.user .message-text li {
    margin-bottom: 2px;
}

.message-row.user .message-text ol {
    list-style-type: decimal;
}

.message-row.user .message-text ul {
    list-style-type: disc;
}

/* Copy Transcript Button */
.copy-transcript-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 12px;
    padding: 5px 10px;
    border-radius: 6px;
    cursor: pointer;
    color: #94a3b8;
    transition: all 0.15s ease;
    user-select: none;
}

.copy-transcript-btn:hover {
    background: #f1f5f9;
    color: #64748b;
}

.copy-transcript-btn svg.copy-icon {
    width: 14px;
    height: 14px;
    display: inline-block;
    vertical-align: middle;
    flex-shrink: 0;
    stroke: currentColor;
}

.copy-transcript-btn .copy-label {
    font-size: 12px;
    letter-spacing: 0.3px;
}
</style>
